/**
 * ARIA Command Center v3.1
 * ────────────────────────
 * Audit-corrected — all API field mappings verified against actual backend
 * response shapes as of 2026-05-17.
 *
 * Key fixes vs v3.0:
 *  - Campaign table: kpis are nested under c.kpis, percentages already ×100
 *  - Tasks: backend field is sla_deadline (not deadline); no assigned_to field
 *  - Agent names: real enum is aria/scout/signal_detector/matchmaker/outreach_captain/deal_closer
 *  - Autopilot: wired to /api/scheduler/run-all and /api/scheduler/run/{job_id}
 *  - Scheduler status panel added to Agents view
 *  - Inbox reply count surfaced in Approvals badge
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import VADashboard from './VADashboard';
import { SendTile, DailySendChart, SourcePointPanel } from './SendVisibility';
import {
  Zap, MessageSquare, Send, CheckCircle, XCircle, Bot,
  AlertTriangle, TrendingUp, DollarSign, RefreshCw, X,
  Loader2, Wifi, WifiOff, LayoutDashboard, ListChecks,
  Megaphone, GitBranch, ChevronRight, ChevronLeft,
  Play, Pause, Activity, Clock, Users, Mail,
  ArrowUpRight, Shield, ThumbsUp, ThumbsDown, Edit3, Target,
  Power, Inbox, BarChart3, Calendar, Tag, Trash2,
  Brain, Sparkles, Award, BookOpen, Star,
  PackageSearch, Rocket, PlusCircle, ExternalLink, FileText,
} from 'lucide-react';
// ─── CONFIG ─────────────────────────────────────────────────────────────────
const API   = process.env.REACT_APP_API_URL || 'https://aria-api-ibja.onrender.com';
const APIKEY = process.env.REACT_APP_API_KEY || '';
// ─── API CLIENT ──────────────────────────────────────────────────────────────
async function api(path, opts = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(APIKEY ? { 'X-API-Key': APIKEY } : {}),
    ...opts.headers,
  };
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(`${API}${path}`, { ...opts, headers });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      return { data: await r.json(), error: null };
    } catch (e) {
      if (attempt === 1) return { data: null, error: e.message };
    }
  }
}
// ─── HELPERS ─────────────────────────────────────────────────────────────────
const money = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toFixed(0)}`;
};
const timeAgo = (iso) => {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso)) / 60_000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};
// Bug 6 fix: timeFrom shows a future-facing relative time (for APScheduler next_run).
// timeAgo() returns "just now" for future dates because the diff is negative.
const timeFrom = (iso) => {
  if (!iso) return '—';
  const mins = Math.round((new Date(iso) - Date.now()) / 60_000);
  if (mins <= 0)    return 'now';
  if (mins < 60)   return `in ${mins}m`;
  if (mins < 1440) return `in ${Math.round(mins / 60)}h`;
  return `in ${Math.round(mins / 1440)}d`;
};
const timeUntil = (iso) => {
  if (!iso) return null;
  const h = Math.round((new Date(iso) - Date.now()) / 3_600_000);
  if (h < 0)  return { label: `${Math.abs(h)}h overdue`, overdue: true };
  if (h < 24) return { label: `${h}h left`, overdue: false };
  return { label: `${Math.round(h / 24)}d left`, overdue: false };
};
/** Backend returns pre-multiplied percentages (e.g. 25.3 means 25.3%) */
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
// ─── MARKDOWN RENDERER (no external deps) ────────────────────────────────────
function Md({ children, className = '' }) {
  if (!children) return null;
  const lines = String(children).split('\n');
  const nodes = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) { nodes.push(<ul key={`ul-${nodes.length}`} className="list-disc list-inside mb-1 space-y-0.5">{listBuf}</ul>); listBuf = []; }
  };
  const inline = (text, key) => {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2,-2)}</strong>;
      if (p.startsWith('*')  && p.endsWith('*'))  return <em key={i}>{p.slice(1,-1)}</em>;
      if (p.startsWith('`')  && p.endsWith('`'))  return <code key={i} className="bg-slate-100 rounded px-1 text-[0.8em] font-mono">{p.slice(1,-1)}</code>;
      return p;
    });
  };
  lines.forEach((line, i) => {
    if (/^---+$/.test(line.trim())) { flushList(); nodes.push(<hr key={i} className="my-2 border-slate-200" />); return; }
    if (line.startsWith('### ')) { flushList(); nodes.push(<h3 key={i} className="font-semibold text-sm mt-2 mb-0.5">{inline(line.slice(4))}</h3>); return; }
    if (line.startsWith('## '))  { flushList(); nodes.push(<h2 key={i} className="font-bold text-sm mt-3 mb-1">{inline(line.slice(3))}</h2>); return; }
    if (line.startsWith('# '))   { flushList(); nodes.push(<h1 key={i} className="font-bold mb-1">{inline(line.slice(2))}</h1>); return; }
    if (/^[-•*] /.test(line))   { listBuf.push(<li key={i}>{inline(line.slice(2))}</li>); return; }
    if (!line.trim())            { flushList(); nodes.push(<br key={i} />); return; }
    flushList();
    nodes.push(<p key={i} className="mb-0.5">{inline(line)}</p>);
  });
  flushList();
  return <div className={`text-sm leading-relaxed ${className}`}>{nodes}</div>;
}
const gradeColor = (g) => ({
  A: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  B: 'bg-green-100   text-green-700   border-green-200',
  C: 'bg-amber-100   text-amber-700   border-amber-200',
  D: 'bg-orange-100  text-orange-700  border-orange-200',
  F: 'bg-red-100     text-red-700     border-red-200',
  N: 'bg-slate-100   text-slate-500   border-slate-200',  // Sprint G Bug 5: "Not enough data" gets grey pill
}[g] || 'bg-slate-100 text-slate-600 border-slate-200');
// ─── TOAST ───────────────────────────────────────────────────────────────────
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const ref = useRef(0);
  const add = useCallback((title, msg = '', type = 'info') => {
    const id = ++ref.current;
    setToasts(p => [...p.slice(-4), { id, title, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 6000);
  }, []);
  const dismiss = useCallback((id) => setToasts(p => p.filter(t => t.id !== id)), []);
  return { toasts, add, dismiss };
}
function Toasts({ toasts, onDismiss }) {
  const cls = { error: 'bg-red-50 border-red-200 text-red-800', warning: 'bg-amber-50 border-amber-200 text-amber-800', success: 'bg-emerald-50 border-emerald-200 text-emerald-800', info: 'bg-blue-50 border-blue-200 text-blue-800' };
  return (
    <div className="fixed top-20 right-4 z-[200] flex flex-col gap-2 max-w-sm w-full">
      {toasts.map(t => (
        <div key={t.id} className={`rounded-xl border shadow-lg px-4 py-3 flex gap-3 items-start ${cls[t.type] || cls.info}`}>
          <div className="flex-1"><p className="text-sm font-semibold">{t.title}</p>{t.msg && <p className="text-xs mt-0.5 opacity-80">{t.msg}</p>}</div>
          <button onClick={() => onDismiss(t.id)} className="opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}
// ─── SHARED UI ───────────────────────────────────────────────────────────────
function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`}>{children}</div>;
}
function CardHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}
function Btn({ onClick, children, variant = 'primary', size = 'sm', disabled = false, loading = false, className = '' }) {
  const base = 'inline-flex items-center gap-1.5 font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed';
  const sz   = { xs: 'px-2.5 py-1 text-xs', sm: 'px-3.5 py-1.5 text-sm', md: 'px-4 py-2 text-sm' };
  const va   = { primary: 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm', success: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm', danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm', ghost: 'bg-slate-100 text-slate-700 hover:bg-slate-200', outline: 'border border-slate-300 text-slate-700 hover:bg-slate-50' };
  return (
    <button onClick={onClick} disabled={disabled || loading} className={`${base} ${sz[size]} ${va[variant]} ${className}`}>
      {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{children}
    </button>
  );
}
function Badge({ label, color = 'slate' }) {
  const c = { slate: 'bg-slate-100 text-slate-600', blue: 'bg-blue-100 text-blue-700', green: 'bg-green-100 text-green-700', emerald: 'bg-emerald-100 text-emerald-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700', purple: 'bg-purple-100 text-purple-700', indigo: 'bg-indigo-100 text-indigo-700', orange: 'bg-orange-100 text-orange-700' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c[color] || c.slate}`}>{label}</span>;
}
function StatusDot({ status }) {
  const d = { running: 'bg-emerald-400 animate-pulse', active: 'bg-emerald-400 animate-pulse', completed: 'bg-blue-400', idle: 'bg-slate-300', error: 'bg-red-400 animate-pulse', paused: 'bg-amber-300' };
  return <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${d[status] || d.idle}`} />;
}
function Spinner() { return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />; }
function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="p-4 bg-slate-100 rounded-2xl mb-3"><Icon className="w-7 h-7 text-slate-400" /></div>
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}
function KpiCard({ label, value, icon: Icon, color, sub, alert }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-xl ${color}`}><Icon className="w-5 h-5" /></div>
        {alert && <span className="text-xs font-medium text-red-600 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />{alert}</span>}
      </div>
      <p className="mt-3 text-2xl font-bold text-slate-900 leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}
// ─── SIDEBAR ─────────────────────────────────────────────────────────────────
const NAV = [
  { id: 'overview',  label: 'Overview',   icon: LayoutDashboard },
  { id: 'approvals', label: 'Approvals',  icon: ListChecks      },
  { id: 'inbox',     label: 'Inbox',      icon: Inbox           },
  { id: 'campaigns', label: 'Campaigns',  icon: Megaphone       },
  { id: 'offers',    label: 'Offers',     icon: Tag             },
  { id: 'pipeline',  label: 'Pipeline',   icon: GitBranch       },
  { id: 'forge',     label: 'FORGE',      icon: PackageSearch   },
  { id: 'launchpad', label: 'LAUNCHPAD',  icon: Rocket          },
  { id: 'agents',    label: 'Agents',     icon: Bot             },
  { id: 'chat',      label: 'ARIA Chat',  icon: MessageSquare   },
];
function Sidebar({ active, onChange, collapsed, onToggle, pendingCount, inboxCount }) {
  return (
    <aside className={`flex flex-col bg-slate-900 text-white transition-all duration-200 ${collapsed ? 'w-16' : 'w-52'} min-h-screen`}>
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-slate-700/50 ${collapsed ? 'justify-center' : ''}`}>
        <div className="p-1.5 bg-indigo-500 rounded-lg flex-shrink-0"><Zap className="w-4 h-4 text-white" /></div>
        {!collapsed && <div><p className="text-sm font-bold">ARIA</p><p className="text-xs text-slate-400">Command Center</p></div>}
      </div>
      <nav className="flex-1 py-4 px-2 space-y-1">
        {NAV.map(({ id, label, icon: Icon }) => {
          const badge = id === 'approvals' ? pendingCount : id === 'inbox' ? inboxCount : 0;
          return (
            <button key={id} onClick={() => onChange(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${active === id ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white hover:bg-slate-800'} ${collapsed ? 'justify-center' : ''}`}>
              <div className="relative flex-shrink-0">
                <Icon className="w-[18px] h-[18px]" />
                {badge > 0 && <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{badge > 9 ? '9+' : badge}</span>}
              </div>
              {!collapsed && <span className="flex-1 truncate text-left">{label}</span>}
              {!collapsed && badge > 0 && <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">{badge}</span>}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-700/50">
        <button onClick={onToggle} className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 text-xs transition-all ${collapsed ? 'justify-center' : ''}`}>
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}
// ─── VIEW: OVERVIEW ──────────────────────────────────────────────────────────
// ─── FLOATING ARIA CHAT ───────────────────────────────────────────────────────
// Persistent bottom-right button + slide-over panel. Available on every view.
function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: 'aria', text: "Hey — I'm ARIA. Ask me anything about the pipeline, campaigns, or what to focus on next." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMsgs(p => [...p, { role: 'jamie', text: msg }]);
    setLoading(true);
    const history = msgs.map(m => ({ role: m.role === 'aria' ? 'assistant' : 'user', content: m.text }));
    const { data, error } = await api('/api/chat', { method: 'POST', body: JSON.stringify({ message: msg, history: history.slice(-10) }) });
    setLoading(false);
    setMsgs(p => [...p, {
      role: 'aria',
      text: data?.response || (error ? `Error: ${error}` : 'No response'),
    }]);
  };

  return (
    <>
      {/* Slide-over panel */}
      {open && (
        <div className="fixed bottom-20 right-5 w-96 max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-[100]"
          style={{ boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">ARIA</p>
              <p className="text-[11px] text-indigo-200">Chief of Staff · Always on</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" style={{ maxHeight: '45vh' }}>
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'jamie' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === 'jamie'
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                }`}>
                  {m.role === 'jamie' ? m.text : <Md>{m.text}</Md>}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <div className="flex gap-1">
                    {[0,1,2].map(i => (
                      <div key={i} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: `${i*0.15}s` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {/* Input — flex-shrink-0 keeps it pinned to bottom even when messages overflow (Bug 6) */}
          <div className="flex-shrink-0 p-3 border-t border-slate-100">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
                placeholder="Ask ARIA anything…"
                className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              />
              <button onClick={send} disabled={!input.trim() || loading}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-xl px-3 py-2 transition-colors">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">Ask about pipeline, campaigns, offers, OKRs</p>
          </div>
        </div>
      )}
      {/* Floating button */}
      <button
        onClick={() => setOpen(p => !p)}
        className="fixed bottom-5 right-5 w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-105 z-[100]"
        title="Ask ARIA"
        style={{ boxShadow: '0 8px 25px rgba(99,102,241,0.5)' }}
      >
        {open ? <X className="w-6 h-6" /> : <Brain className="w-6 h-6" />}
      </button>
    </>
  );
}

// ─── OKR QUICK CARD ───────────────────────────────────────────────────────────
function OKRQuickCard({ onNavigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api('/api/okrs/current').then(({ data }) => { if (data?.cycle) setData(data); });
  }, []);
  if (!data) return null;

  const { cycle, objectives = [], cycle_progress = 0 } = data;
  const statusColour = { on_track: '#27ae60', at_risk: '#f39c12', off_track: '#e74c3c', achieved: '#2980b9' };
  const statusEmoji = { on_track: '🟢', at_risk: '🟡', off_track: '🔴', achieved: '✅' };

  return (
    <Card>
      <CardHeader
        title={`🎯 OKRs — Q${cycle.quarter} ${cycle.year}`}
        subtitle={`${cycle_progress.toFixed(0)}% cycle progress`}
        action={<Btn variant="ghost" size="xs" onClick={() => onNavigate && onNavigate('pipeline')}>Details <ChevronRight className="w-3 h-3" /></Btn>}
      />
      <div className="px-5 pb-5 space-y-3">
        {objectives.slice(0, 3).map(obj => (
          <div key={obj.id}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-slate-700 truncate flex-1 pr-2">{obj.title}</p>
              <span className="text-xs text-slate-500 flex-shrink-0">{(obj.overall_progress || 0).toFixed(0)}%</span>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full">
              <div className="h-1.5 rounded-full bg-indigo-500 transition-all"
                style={{ width: `${Math.min(100, obj.overall_progress || 0)}%` }} />
            </div>
            <div className="flex gap-2 mt-1.5 flex-wrap">
              {(obj.key_results || []).slice(0, 3).map(kr => (
                <span key={kr.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-600">
                  {statusEmoji[kr.status] || '⚪'} {kr.title.split(' ').slice(0, 4).join(' ')}…
                </span>
              ))}
            </div>
          </div>
        ))}
        {objectives.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-2">Run migration 32-okrs.sql to seed OKRs</p>
        )}
      </div>
    </Card>
  );
}

// ─── ACTIVE OFFERS QUICK CARD ─────────────────────────────────────────────────
function ActiveOffersCard({ onNavigate }) {
  const [roster, setRoster] = useState([]);
  const [pending, setPending] = useState(0);
  useEffect(() => {
    Promise.all([
      api('/api/offer-os/active-roster'),
      api('/api/offer-os?status=pending_review'),
    ]).then(([r, p]) => {
      setRoster(r.data?.active_offers || r.data?.active_roster || (Array.isArray(r.data) ? r.data : []));
      const pr = p.data?.offers || p.data || [];
      setPending(Array.isArray(pr) ? pr.length : 0);
    });
  }, []);

  const SCORE_COLOR = s => s >= 75 ? 'text-emerald-600' : s >= 50 ? 'text-amber-600' : 'text-red-500';

  return (
    <Card>
      <CardHeader
        title="📦 Active Offer Roster"
        subtitle={`${roster.length} live${pending > 0 ? ` · ${pending} pending review` : ''}`}
        action={<Btn variant="ghost" size="xs" onClick={() => onNavigate('offers')}>Manage <ChevronRight className="w-3 h-3" /></Btn>}
      />
      <div className="divide-y divide-slate-100">
        {!Array.isArray(roster) || roster.length === 0 ? (
          <div className="px-5 py-6 text-center">
            <Tag className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-400">No active offers yet.</p>
            <Btn variant="outline" size="xs" className="mt-2" onClick={() => onNavigate('offers')}>Submit first offer</Btn>
          </div>
        ) : (Array.isArray(roster) ? roster : []).map((o, i) => (
          <div key={i} className="px-5 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Star className="w-4 h-4 text-indigo-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{o.product_name || o.name || 'Offer'}</p>
              <p className="text-xs text-slate-500 truncate">{o.vertical || o.category || '—'}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className={`text-xs font-bold ${SCORE_COLOR(o.aria_score || 0)}`}>{o.aria_score || '—'}</p>
              <p className="text-[10px] text-slate-400">ARIA score</p>
            </div>
          </div>
        ))}
        {pending > 0 && (
          <div className="px-5 py-2.5 flex items-center gap-2 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => onNavigate('offers')}>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            <p className="text-xs font-medium text-amber-700">{pending} offer{pending > 1 ? 's' : ''} awaiting ARIA review</p>
            <ChevronRight className="w-3.5 h-3.5 text-amber-500 ml-auto" />
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── REFERRAL NUDGE CARD ──────────────────────────────────────────────────────
function ReferralNudgeCard({ onNavigate }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api('/api/referrals/summary').then(({ data }) => { if (data) setSummary(data); });
  }, []);
  if (!summary || summary.eligible_pending_outreach === 0) return null;
  return (
    <div
      className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all"
      onClick={() => onNavigate('pipeline')}
    >
      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
        <Users className="w-5 h-5 text-emerald-600" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-emerald-800">
          🤝 {summary.eligible_pending_outreach} client{summary.eligible_pending_outreach > 1 ? 's' : ''} ready for a referral ask
        </p>
        <p className="text-xs text-emerald-600 mt-0.5">
          90+ days post-conversion · {summary.referrals_made || 0} referrals made so far
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-emerald-500 flex-shrink-0" />
    </div>
  );
}

// ─── ARIA MEMORY PULSE ────────────────────────────────────────────────────────
// Shows latest journal headline + what ARIA is focused on today
function MemoryPulseCard() {
  const [mem, setMem] = useState(null);
  useEffect(() => {
    api('/api/memory/dashboard').then(({ data }) => { if (data && !data.error) setMem(data); });
  }, []);
  if (!mem?.latest_journal?.headline) return null;
  return (
    <div className="bg-gradient-to-r from-indigo-900 to-purple-900 rounded-2xl p-5 text-white">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Brain className="w-5 h-5 text-indigo-200" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs font-semibold text-indigo-300 uppercase tracking-wide">ARIA's Daily Read</p>
            <span className="text-[10px] text-indigo-400">{mem.latest_journal.date}</span>
          </div>
          <p className="text-sm font-medium text-white leading-snug">{mem.latest_journal.headline}</p>
          {mem.latest_journal.recommendation && (
            <p className="text-xs text-indigo-300 mt-1.5 flex items-start gap-1">
              <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>Today: {mem.latest_journal.recommendation}</span>
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/10">
        <span className="text-[11px] text-indigo-400">{mem.episodes_last_7_days} events this week</span>
        <span className="text-[11px] text-indigo-400">{mem.active_patterns} learned patterns</span>
        <span className="text-[11px] text-indigo-400">{mem.prospects_in_memory} prospects known</span>
      </div>
    </div>
  );
}

function OverviewView({ kpis, tasks, agents, report, reportLoading, onNavigate, onApprove, onReject, actioning }) {
  const urgent = tasks.filter(t => t.status === 'pending').slice(0, 3);
  const runningAgents = agents.filter(a => a.last_run_status === 'running').length;
  // Sprint G Bug 4: agents don't self-heartbeat; show runs in last 24h instead of "online" count
  const agentRunsToday = agents.filter(a => a.last_run_at && new Date(a.last_run_at) > new Date(Date.now() - 86400000)).length;
  // Real agent label/emoji map matching backend AgentName enum
  const AGENT_META = {
    aria:              { label: 'ARIA (COO)',        emoji: '🧠' },
    scout:             { label: 'Scout',             emoji: '🔍' },
    signal_detector:   { label: 'Signal Detector',   emoji: '📡' },
    matchmaker:        { label: 'Matchmaker',        emoji: '🤝' },
    outreach_captain:  { label: 'Outreach Captain',  emoji: '📧' },
    deal_closer:       { label: 'Deal Closer',       emoji: '🏆' },
  };
  return (
    <div className="space-y-6">
      {/* ARIA Memory Pulse — latest journal headline */}
      <MemoryPulseCard />

      {/* Referral nudge — only shows when eligible clients exist */}
      <ReferralNudgeCard onNavigate={onNavigate} />

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
        <KpiCard label="Pipeline Value"      value={money(kpis.pipeline_value)}       icon={TrendingUp}  color="bg-blue-50 text-blue-600" />
        <KpiCard label="Revenue MTD"         value={money(kpis.commission_this_month)} icon={DollarSign}  color="bg-emerald-50 text-emerald-600" sub={`${money(kpis.commission_ytd)} YTD`} />
        <SendTile />
        <KpiCard label="Leads in Pipeline"   value={(kpis.leads_in_pipeline ?? 0).toLocaleString()} icon={TrendingUp} color="bg-violet-50 text-violet-600" sub="active + pitched" />
        <KpiCard label="Active Campaigns"    value={kpis.campaigns_active ?? 0}        icon={Megaphone}   color="bg-indigo-50 text-indigo-600" />
        <KpiCard label="Pending Approvals"   value={kpis.tasks_pending ?? 0}           icon={ListChecks}  color={kpis.tasks_overdue > 0 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'} alert={kpis.tasks_overdue > 0 ? `${kpis.tasks_overdue} overdue` : null} />
        <KpiCard label="Agent Runs Today"    value={agentRunsToday} icon={Bot} color="bg-purple-50 text-purple-600" sub={kpis.ai_cost_this_month ? `$${kpis.ai_cost_this_month} AI spend MTD` : 'last 24h'} />
      </div>

      {/* Send Visibility: 30-day chart (2/3) + SourcePoint panel (1/3) */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2"><DailySendChart /></div>
        <div className="xl:col-span-1"><SourcePointPanel /></div>
      </div>

      {/* Main content + sidebar */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Daily briefing */}
        <Card className="xl:col-span-3">
          <CardHeader title="ARIA's Daily Briefing" subtitle="Generated from live campaign data" />
          <div className="p-5">
            {reportLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : report ? (
              <Md className="text-slate-700">{report}</Md>
            ) : (
              <EmptyState icon={Zap} title="No briefing yet" subtitle="ARIA generates the morning briefing at 7 AM UTC via the scheduler" />
            )}
          </div>
        </Card>
        {/* Urgent approvals */}
        <Card className="xl:col-span-2">
          <CardHeader title="Needs Attention" subtitle={`${urgent.length} pending`}
            action={<Btn variant="ghost" size="xs" onClick={() => onNavigate('approvals')}>All <ChevronRight className="w-3 h-3" /></Btn>} />
          <div className="divide-y divide-slate-100">
            {urgent.length === 0
              ? <div className="px-5 py-8"><EmptyState icon={CheckCircle} title="All clear" subtitle="No pending approvals" /></div>
              : urgent.map(t => <MiniTaskCard key={t.id} task={t} onApprove={onApprove} onReject={onReject} actioning={actioning} />)
            }
          </div>
        </Card>
      </div>

      {/* OKRs + Active Offers row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <OKRQuickCard onNavigate={onNavigate} />
        <ActiveOffersCard onNavigate={onNavigate} />
      </div>

      {/* Agent status strip — uses real backend AgentName values */}
      <Card>
        <CardHeader title="Agent Swarm Status" subtitle="Last run from DB · click Agents to trigger manually"
          action={<Btn variant="ghost" size="xs" onClick={() => onNavigate('agents')}>Manage <ChevronRight className="w-3 h-3" /></Btn>} />
        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {agents.length === 0
            ? [1,2,3,4,5,6].map(i => <div key={i} className="h-24 bg-slate-50 rounded-xl animate-pulse" />)
            : agents.map(a => {
                const meta = AGENT_META[a.agent] || { label: a.agent, emoji: '🤖' };
                return (
                  <div key={a.agent} className="flex flex-col items-center text-center gap-1.5 p-3 bg-slate-50 rounded-xl">
                    <span className="text-xl">{meta.emoji}</span>
                    <StatusDot status={a.last_run_status || 'idle'} />
                    <p className="text-xs font-medium text-slate-700 leading-tight">{meta.label}</p>
                    <p className="text-[11px] text-slate-400">{timeAgo(a.last_run_at)}</p>
                    {a.monthly_spend > 0 && <p className="text-[10px] text-slate-400">${a.monthly_spend?.toFixed(2)}/mo</p>}
                  </div>
                );
              })
          }
        </div>
      </Card>
    </div>
  );
}
// MiniTaskCard — uses sla_deadline (backend field name), no assigned_to
function MiniTaskCard({ task, onApprove, onReject, actioning }) {
  const tl = timeUntil(task.sla_deadline);
  const PB = { time_sensitive: { label: 'Urgent', color: 'red' }, high: { label: 'High', color: 'amber' }, medium: { label: 'Medium', color: 'blue' }, low: { label: 'Low', color: 'slate' } };
  const pb = PB[task.priority] || { label: task.priority, color: 'slate' };
  return (
    <div className="px-5 py-3.5">
      <div className="flex items-start gap-2 mb-1.5">
        <p className="text-sm font-medium text-slate-800 flex-1 leading-snug">{task.title}</p>
        <Badge label={pb.label} color={pb.color} />
      </div>
      {task.description && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{task.description}</p>}
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-400 flex items-center gap-1">
          {tl && <span className={tl.overdue ? 'text-red-600 font-medium' : ''}><Clock className="w-3 h-3 inline mr-0.5" />{tl.label}</span>}
          {task.agent_source && <span className="ml-2 capitalize">via {task.agent_source}</span>}
        </div>
        <div className="flex gap-1.5">
          <Btn variant="success" size="xs" loading={actioning === `${task.id}-approve`} onClick={() => onApprove(task.id)}><ThumbsUp className="w-3 h-3" /> OK</Btn>
          <Btn variant="danger"  size="xs" loading={actioning === `${task.id}-reject`}  onClick={() => onReject(task.id)}><ThumbsDown className="w-3 h-3" /></Btn>
        </div>
      </div>
    </div>
  );
}
// ─── CAMPAIGN COPY REVIEW PANEL ──────────────────────────────────────────────
function PendingCampaignPanel({ toast }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api('/api/ttc/pending-campaigns');
    setPending(data?.pending || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const confirm = async (token, slug) => {
    setConfirming(token);
    const { data, error } = await api(`/api/ttc/confirm-launch?token=${encodeURIComponent(token)}`);
    setConfirming(null);
    if (data) {
      toast('Campaign launched! ✓', `${data.campaigns?.length || 0} campaign(s) pushed to Instantly — activate in Instantly dashboard`, 'success');
      load();
    } else toast('Launch failed', error, 'error');
  };
  if (loading) return null;
  if (pending.length === 0) return null;
  return (
    <div className="space-y-3 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="w-4 h-4 text-indigo-600" />
        <h3 className="text-sm font-semibold text-slate-900">Campaign Copy Awaiting Approval</h3>
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{pending.length}</span>
      </div>
      {pending.map((p) => (
        <Card key={p.payload_key} className="border-l-4 border-l-indigo-500">
          <div className="p-5">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">AIVideo Agency Campaign — {p.total_leads} leads</p>
                <p className="text-xs text-slate-500 mt-0.5">Verticals: {p.verticals?.join(', ')}</p>
                <p className="text-xs text-slate-400 mt-0.5">Queued {timeAgo(p.requested_at)} · expires in 24h</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <a href={p.review_url} target="_blank" rel="noopener noreferrer">
                  <Btn variant="outline" size="sm"><Edit3 className="w-3.5 h-3.5" /> Review Copy</Btn>
                </a>
                <Btn variant="success" size="sm" loading={confirming === p.payload_key}
                  onClick={() => confirm(p.review_url?.split('token=')[1] || p.payload_key, p.payload_key)}>
                  <ThumbsUp className="w-3.5 h-3.5" /> Approve & Launch
                </Btn>
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-xs text-indigo-800">
              <p className="font-semibold mb-1">Email 1 preview (Agency Partner pitch):</p>
              <p className="italic">"Do any of your clients need better video for their marketing? You refer a client, they buy, you earn 15% commission on every order…"</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
// ─── VIEW: APPROVALS ─────────────────────────────────────────────────────────
function ApprovalsView({ tasks, onApprove, onReject, actioning, toast }) {
  const [filter, setFilter] = useState('pending');
  const [noteMap, setNoteMap] = useState({});
  const [dismissing, setDismissing] = useState(false);
  const [localTasks, setLocalTasks] = useState(tasks);
  // Keep local copy in sync with parent tasks prop
  React.useEffect(() => { setLocalTasks(tasks); }, [tasks]);
  const filtered = localTasks.filter(t => {
    if (filter === 'pending')  return t.status === 'pending';
    if (filter === 'overdue')  return t.is_overdue;
    if (filter === 'approved') return t.status === 'approved';
    if (filter === 'rejected') return t.status === 'rejected';
    return true;
  });
  const PRI = { time_sensitive: 0, high: 1, medium: 2, low: 3 };
  const sorted = [...filtered].sort((a, b) => (PRI[a.priority] ?? 9) - (PRI[b.priority] ?? 9));
  const FILTERS = [{ id: 'pending', label: 'Pending' }, { id: 'overdue', label: '⚠ Overdue' }, { id: 'approved', label: 'Approved' }, { id: 'rejected', label: 'Rejected' }];
  // Bug 2 fix: bulk-dismiss all pending tasks older than 48h
  const handleBulkDismiss = async () => {
    if (!window.confirm('Dismiss all pending tasks older than 48 hours? This clears ghost tasks from n8n.')) return;
    setDismissing(true);
    const { data, error } = await api('/api/tasks/bulk-dismiss', { method: 'POST' });
    setDismissing(false);
    if (data) {
      toast(`Dismissed ${data.dismissed} stale tasks`, '', 'success');
      // Remove dismissed tasks from local view immediately
      setLocalTasks(p => p.filter(t => t.status !== 'pending'));
    } else {
      toast('Dismiss failed', error || '', 'error');
    }
  };
  const pendingCount = localTasks.filter(t => t.status === 'pending').length;
  return (
    <div className="space-y-5">
      <PendingCampaignPanel toast={toast} />
      <Card className="p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {FILTERS.map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filter === f.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-sm text-slate-500">{sorted.length} task{sorted.length !== 1 ? 's' : ''}</span>
          {/* Bug 2 fix: one-click clear of ghost n8n tasks */}
          {pendingCount > 0 && (
            <Btn variant="danger" size="sm" loading={dismissing} onClick={handleBulkDismiss} className="ml-auto">
              <Trash2 className="w-3.5 h-3.5" /> Bulk Dismiss All ({pendingCount})
            </Btn>
          )}
        </div>
      </Card>
      {sorted.length === 0
        ? <Card className="p-12"><EmptyState icon={CheckCircle} title="Nothing here" subtitle="No tasks match this filter" /></Card>
        : sorted.map(task => (
            <FullTaskCard key={task.id} task={task} onApprove={onApprove} onReject={onReject} actioning={actioning}
              note={noteMap[task.id] || ''} onNoteChange={v => setNoteMap(p => ({ ...p, [task.id]: v }))} />
          ))
      }
    </div>
  );
}
function FullTaskCard({ task, onApprove, onReject, actioning, note, onNoteChange }) {
  // Backend field: sla_deadline (not deadline)
  const tl = timeUntil(task.sla_deadline);
  const [showNote, setShowNote] = useState(false);
  const CAT_ICON = { campaign_launch: Megaphone, deal_evaluation: Target, outreach_decision: Mail, lead_qualification: Users, system_alert: Shield };
  const CatIcon = CAT_ICON[task.category] || ListChecks;
  const PRI = { time_sensitive: 'bg-red-100 text-red-700', high: 'bg-amber-100 text-amber-700', medium: 'bg-blue-100 text-blue-700', low: 'bg-slate-100 text-slate-600' };
  const isDone = ['approved', 'rejected'].includes(task.status);
  return (
    <Card className={isDone ? 'opacity-60' : ''}>
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="p-2.5 bg-slate-100 rounded-xl flex-shrink-0 mt-0.5"><CatIcon className="w-4 h-4 text-slate-600" /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 flex-wrap mb-1">
              <h4 className="text-sm font-semibold text-slate-900 flex-1">{task.title}</h4>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${PRI[task.priority] || PRI.low}`}>{task.priority?.replace('_', ' ')}</span>
              {/* agent_source shown as attribution since no assigned_to field */}
              {task.agent_source && <Badge label={`from ${task.agent_source}`} color="slate" />}
              {isDone && <Badge label={task.status} color={task.status === 'approved' ? 'green' : 'red'} />}
            </div>
            {task.description && <p className="text-sm text-slate-600 leading-relaxed mb-3">{task.description}</p>}
            {task.aria_recommendation && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 mb-3">
                <p className="text-xs font-semibold text-indigo-600 mb-1">ARIA Recommends</p>
                <Md className="text-indigo-800">{task.aria_recommendation}</Md>
              </div>
            )}
            <div className="flex items-center gap-4 flex-wrap text-xs text-slate-400 mb-3">
              {tl && <span className={`flex items-center gap-1 ${tl.overdue ? 'text-red-600 font-medium' : ''}`}><Clock className="w-3 h-3" />{tl.label}</span>}
              {task.created_at && <span>Created {timeAgo(task.created_at)}</span>}
              {task.category && <span className="capitalize">{task.category.replace('_', ' ')}</span>}
            </div>
            {showNote && !isDone && (
              <textarea value={note} onChange={e => onNoteChange(e.target.value)}
                placeholder="Add a note (optional)…"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none h-16 text-slate-700 mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            )}
          </div>
          {!isDone && (
            <div className="flex flex-col gap-2 flex-shrink-0">
              <Btn variant="success" size="sm" loading={actioning === `${task.id}-approve`} onClick={() => onApprove(task.id, note)}><ThumbsUp className="w-3.5 h-3.5" /> Approve</Btn>
              <Btn variant="danger"  size="sm" loading={actioning === `${task.id}-reject`}  onClick={() => onReject(task.id, note)}><ThumbsDown className="w-3.5 h-3.5" /> Reject</Btn>
              <Btn variant="ghost" size="xs" onClick={() => setShowNote(p => !p)}><Edit3 className="w-3 h-3" /> Note</Btn>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
// ─── VIEW: INBOX ─────────────────────────────────────────────────────────────
const INTENT_EMOJI = { interested: '🔥', meeting_request: '📅', question: '❓', not_interested: '🙅', unsubscribe: '🚫', other: '📬' };
const INTENT_COLOR = { interested: 'emerald', not_interested: 'red', question: 'blue', referral: 'purple', meeting_request: 'amber', objection: 'orange', unsubscribe: 'red', bounce: 'slate', other: 'slate' };
const HOT_INTENTS  = new Set(['interested', 'meeting_request', 'question']);
function HotLeadCard({ lead, onApproved }) {
  const [sending, setSending] = useState(false);
  const intent = lead.classification || lead.intent || 'other';
  const emoji  = INTENT_EMOJI[intent] || '📬';
  const approveDraft = async () => {
    if (!lead.approve_url && !lead.id) return;
    setSending(true);
    // Call backend approve endpoint directly (token not available client-side — open in new tab)
    // For dashboard approval we use the JSON API endpoint via reply_id
    const { data, error } = await api(`/api/inbox/replies/${lead.id}/approve-response`, { method: 'POST' });
    setSending(false);
    if (data) onApproved(lead.id);
    else alert('Send failed: ' + (error || 'unknown error'));
  };
  return (
    <div className="bg-white border-2 border-emerald-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{emoji}</span>
          <div>
            <p className="font-semibold text-slate-800 text-sm">{lead.from_name || lead.from_email}</p>
            <p className="text-xs text-slate-500">{lead.from_email} · {timeAgo(lead.received_at)}</p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Badge label={intent.replace('_', ' ')} color={INTENT_COLOR[intent] || 'slate'} />
          {lead.lead_type && <Badge label={lead.lead_type.toUpperCase()} color="blue" />}
        </div>
      </div>
      {lead.body_preview && (
        <div className="bg-slate-50 border-l-4 border-slate-300 rounded-r-lg px-3 py-2 text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">
          {lead.body_preview}
        </div>
      )}
      {lead.suggested_response && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-3">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">ARIA's Draft Reply</p>
          <Md className="text-slate-700">{lead.suggested_response}</Md>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {lead.suggested_response && (
          <Btn variant="primary" size="sm" onClick={approveDraft} loading={sending}>
            ✅ Send ARIA's Reply
          </Btn>
        )}
        <a href={`mailto:${lead.from_email}?subject=Re: ${encodeURIComponent(lead.subject || '')}`}
           className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
          ✉️ Reply Yourself
        </a>
      </div>
    </div>
  );
}
// ─── INBOX DETAIL DRAWER ─────────────────────────────────────────────────────
function InboxDetailDrawer({ reply, onClose, onApproved }) {
  const [sending, setSending] = useState(false);
  if (!reply) return null;
  const intent = reply.intent || reply.classification || 'other';
  const approve = async () => {
    if (!reply.id) return;
    setSending(true);
    const { data, error } = await fetch(`${process.env.REACT_APP_API_URL || 'https://aria-api-ibja.onrender.com'}/api/inbox/replies/${reply.id}/approve-response`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(process.env.REACT_APP_API_KEY ? { 'X-API-Key': process.env.REACT_APP_API_KEY } : {}) } })
      .then(r => r.json().then(d => ({ data: d, error: null })))
      .catch(e => ({ data: null, error: e.message }));
    setSending(false);
    if (data) { onApproved && onApproved(reply.id); onClose(); }
    else alert('Send failed: ' + (error || 'unknown'));
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="font-semibold text-slate-800">{reply.from_name || reply.from_email || '—'}</p>
            <p className="text-xs text-slate-400">{reply.from_email} · {timeAgo(reply.received_at || reply.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge label={intent.replace('_',' ')} color={INTENT_COLOR[intent] || 'slate'} />
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"><X className="w-4 h-4 text-slate-500" /></button>
          </div>
        </div>
        {/* Subject */}
        {reply.subject && <div className="px-5 py-3 border-b border-slate-100 bg-slate-50"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Subject</p><p className="text-sm text-slate-700">{reply.subject}</p></div>}
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {reply.body_preview && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Email Body</p>
              <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{reply.body_preview}</div>
            </div>
          )}
          {reply.suggested_response && (
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2">ARIA's Draft Reply</p>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                <Md className="text-slate-700">{reply.suggested_response}</Md>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-xs">
            {reply.lead_type && <div className="bg-slate-50 rounded-lg p-3"><p className="text-slate-400 mb-0.5">Offer Type</p><p className="font-medium text-slate-700 capitalize">{reply.lead_type}</p></div>}
            {reply.status && <div className="bg-slate-50 rounded-lg p-3"><p className="text-slate-400 mb-0.5">Status</p><p className="font-medium text-slate-700 capitalize">{reply.status.replace('_',' ')}</p></div>}
          </div>
        </div>
        {/* Actions */}
        <div className="px-5 py-4 border-t border-slate-100 flex gap-3">
          {reply.suggested_response && (
            <button onClick={approve} disabled={sending} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {sending ? '⏳ Sending…' : '✅ Send ARIA\'s Reply'}
            </button>
          )}
          <a href={`mailto:${reply.from_email}?subject=Re: ${encodeURIComponent(reply.subject || '')}`}
             className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors">
            ✉️ Reply Yourself
          </a>
        </div>
      </div>
    </div>
  );
}
function InboxView({ toast }) {
  const [hotLeads, setHotLeads] = useState([]);
  const [replies,  setReplies]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [scanning, setScanning] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const [selectedReply, setSelectedReply] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: hotData }, { data: allData }] = await Promise.all([
      api('/api/inbox/hot-leads'),
      api('/api/inbox/replies/all'),
    ]);
    if (hotData?.hot_leads) setHotLeads(hotData.hot_leads);
    if (allData?.replies)   setReplies(allData.replies);
    else if (Array.isArray(allData)) setReplies(allData);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const scanNow = async () => {
    setScanning(true);
    const { data, error } = await api('/api/inbox/scan', { method: 'POST' });
    setScanning(false);
    if (data) { toast('Scan complete', data.message || `${data.classified || 0} emails classified`, 'success'); load(); }
    else toast('Scan failed', error, 'error');
  };
  const handleApproved = (id) => {
    setDismissed(prev => new Set([...prev, id]));
    toast('Reply sent', 'ARIA\'s response has been sent to the prospect', 'success');
    load();
  };
  const visibleHot = hotLeads.filter(l => !dismissed.has(l.id));
  return (
    <div className="space-y-6">
      {/* Action bar */}
      <div className="flex items-center gap-3">
        <Btn variant="primary" size="md" onClick={scanNow} loading={scanning}><Activity className="w-4 h-4" /> Scan Inbox Now</Btn>
        <Btn variant="ghost" size="md" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Btn>
        <span className="text-sm text-slate-500">{replies.length} total replies</span>
        {visibleHot.length > 0 && (
          <span className="px-2.5 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full animate-pulse">
            🔥 {visibleHot.length} HOT — Act Now
          </span>
        )}
      </div>
      {/* HOT LEADS — act now */}
      {loading ? null : visibleHot.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            🔥 Hot Leads — Needs Your Reply
            <span className="text-xs font-normal text-slate-400 normal-case">ARIA has drafted a response for each</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {visibleHot.map(lead => (
              <HotLeadCard key={lead.id} lead={lead} onApproved={handleApproved} />
            ))}
          </div>
        </div>
      )}
      {/* ALL REPLIES TABLE */}
      <Card>
        <CardHeader title="All Classified Replies" subtitle="Cross-vertical: TTC + AIVideo + ACX" />
        {loading ? <div className="flex justify-center py-16"><Spinner /></div>
          : replies.length === 0 ? <div className="p-8"><EmptyState icon={Inbox} title="No replies yet" subtitle="Run a scan or wait for Instantly to deliver replies" /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {['From', 'Subject', 'Intent', 'Offer', 'Status', 'Received'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {replies.slice(0, 60).map((r, i) => {
                    const cls = r.intent || r.classification || 'other';
                    const isHot = HOT_INTENTS.has(cls);
                    return (
                      <tr key={i}
                        onClick={() => setSelectedReply(r)}
                        className={`cursor-pointer hover:bg-slate-50 active:bg-slate-100 transition-colors ${isHot && r.status !== 'response_sent' ? 'bg-emerald-50/40' : ''}`}>
                        <td className="px-5 py-3 font-medium text-slate-800 max-w-[160px] truncate">
                          {isHot && r.status !== 'response_sent' && <span className="mr-1">{INTENT_EMOJI[cls]}</span>}
                          {r.from_name || r.from_email || '—'}
                        </td>
                        <td className="px-5 py-3 text-slate-600 max-w-[200px] truncate">{r.subject || '—'}</td>
                        <td className="px-5 py-3"><Badge label={cls.replace('_',' ')} color={INTENT_COLOR[cls] || 'slate'} /></td>
                        <td className="px-5 py-3 text-slate-500 capitalize">{r.lead_type || r.offer_type || '—'}</td>
                        <td className="px-5 py-3"><Badge label={r.status || 'classified'} color={r.status === 'response_sent' ? 'emerald' : 'slate'} /></td>
                        <td className="px-5 py-3 text-slate-400 text-xs">{timeAgo(r.received_at || r.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </Card>
      <InboxDetailDrawer
        reply={selectedReply}
        onClose={() => setSelectedReply(null)}
        onApproved={(id) => { setSelectedReply(null); handleApproved(id); }}
      />
    </div>
  );
}
// ─── VIEW: OFFERS ────────────────────────────────────────────────────────────
// Manage per-offer booking URLs, ARIA context, and commission %.
// Each row in this table = one sellable offer. ARIA auto-selects the right one per campaign.
// ─── OFFER OS SECTION ────────────────────────────────────────────────────────
const STATUS_COLOR = {
  active:           'emerald',
  pending_review:   'amber',
  researching:      'blue',
  queued:           'indigo',
  paused:           'slate',
  rejected:         'red',
};
function OfferOsSection({ toast }) {
  const [roster,   setRoster]   = useState([]);
  const [pending,  setPending]  = useState([]);
  const [ranking,  setRanking]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(null);   // offer_id being acted on
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState({ product_name: '', affiliate_url: '', submitter_notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    const [r1, r2, r3] = await Promise.all([
      api('/api/offer-os/active-roster'),
      api('/api/offer-os?status=pending_review'),
      api('/api/offer-os/portfolio-ranking'),
    ]);
    if (r1.data?.roster)  setRoster(r1.data.roster);
    if (r2.data?.offers)  setPending(r2.data.offers);
    if (r3.data?.ranking) setRanking(r3.data.ranking);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (offerId, action, body = {}) => {
    setActing(offerId);
    const { data, error } = await api(`/api/offer-os/${offerId}/${action}`, { method: 'POST', body: JSON.stringify(body) });
    setActing(null);
    if (data) { toast('Done', `Offer ${action}d`, 'success'); load(); }
    else toast('Error', error, 'error');
  };
  const submitOffer = async () => {
    if (!form.product_name || !form.affiliate_url) { toast('Missing fields', 'Product name and URL are required', 'error'); return; }
    setSubmitting(true);
    const { data, error } = await api('/api/offer-os/submit', { method: 'POST', body: JSON.stringify(form) });
    setSubmitting(false);
    if (data) {
      toast('Submitted!', `ARIA is researching "${form.product_name}"`, 'success');
      setShowForm(false); setForm({ product_name: '', affiliate_url: '', submitter_notes: '' }); load();
    } else toast('Submit failed', error, 'error');
  };
  const scoreBar = (score) => (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${score || 0}%` }} />
      </div>
      <span className="text-xs font-mono text-slate-500 w-7 text-right">{score ?? '—'}</span>
    </div>
  );
  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold text-slate-900 flex-1">ARIA Offer OS</h2>
        <Btn variant="primary" size="md" onClick={() => setShowForm(v => !v)}>
          <Tag className="w-4 h-4" /> Submit Offer
        </Btn>
        <Btn variant="ghost" size="md" onClick={load}><RefreshCw className="w-4 h-4" /></Btn>
        <a href={`${API}/api/offer-os/intake`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
          <ArrowUpRight className="w-4 h-4" /> Emily's Form
        </a>
      </div>
      {/* Inline submit form */}
      {showForm && (
        <Card>
          <CardHeader title="Submit New Offer" subtitle="ARIA will research, generate ICP + email sequences, and queue for approval" />
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Product Name *</label>
                <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. Instantly.ai" value={form.product_name}
                  onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Affiliate URL *</label>
                <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
                  placeholder="https://instantly.ai/ref/..." value={form.affiliate_url}
                  onChange={e => setForm(f => ({ ...f, affiliate_url: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Notes for ARIA (optional target market, positioning ideas)</label>
              <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Target agencies doing cold outreach for clients, focus on teams of 3-10 people..."
                value={form.submitter_notes} onChange={e => setForm(f => ({ ...f, submitter_notes: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Btn variant="primary" size="sm" onClick={submitOffer} loading={submitting}>Submit to ARIA</Btn>
              <Btn variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Btn>
            </div>
          </div>
        </Card>
      )}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <>
          {/* Active Roster — 3 slots */}
          <Card>
            <CardHeader title="Active Roster" subtitle="Up to 3 offers in live campaigns. ARIA rotates based on performance score." />
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
              {[1, 2, 3].map(slot => {
                const offer = roster.find(o => o.roster_position === slot);
                return (
                  <div key={slot} className="p-5">
                    <p className="text-xs font-semibold text-slate-400 mb-3">SLOT {slot}</p>
                    {offer ? (
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{offer.product_name}</p>
                            <a href={offer.affiliate_url} target="_blank" rel="noreferrer"
                               className="text-xs text-blue-500 hover:underline font-mono truncate block max-w-[200px]">
                              {offer.affiliate_url?.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                          <Badge label="active" color="emerald" />
                        </div>
                        {scoreBar(offer.aria_score)}
                        <div className="grid grid-cols-3 gap-1 text-center">
                          <div><p className="text-xs text-slate-400">Contacted</p><p className="text-sm font-semibold">{offer.total_contacted ?? 0}</p></div>
                          <div><p className="text-xs text-slate-400">Replies</p><p className="text-sm font-semibold">{offer.total_replies ?? 0}</p></div>
                          <div><p className="text-xs text-slate-400">Converted</p><p className="text-sm font-semibold">{offer.total_conversions ?? 0}</p></div>
                        </div>
                        <div className="flex gap-1.5">
                          <Btn variant="ghost" size="xs" onClick={() => act(offer.id, 'queue')} loading={acting === offer.id}>Queue</Btn>
                          <Btn variant="ghost" size="xs" onClick={() => act(offer.id, 'pause')} loading={acting === offer.id}><Pause className="w-3 h-3" /></Btn>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-24 text-slate-400">
                        <Target className="w-6 h-6 mb-1" />
                        <p className="text-xs">Empty slot</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
          {/* Pending Review */}
          {pending.length > 0 && (
            <Card>
              <CardHeader title={`Pending ARIA Review (${pending.length})`} subtitle="ARIA is researching these — approve to add to roster" />
              <div className="divide-y divide-slate-100">
                {pending.map(offer => (
                  <div key={offer.id} className="px-5 py-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium text-slate-800 text-sm">{offer.product_name}</p>
                        <Badge label={offer.status} color={STATUS_COLOR[offer.status] || 'slate'} />
                      </div>
                      <a href={offer.affiliate_url} target="_blank" rel="noreferrer"
                         className="text-xs text-blue-500 font-mono hover:underline">
                        {offer.affiliate_url?.replace(/^https?:\/\//, '')}
                      </a>
                      {offer.icp_description && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-1">{offer.icp_description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {offer.aria_score != null && (
                        <div className="text-center">
                          <p className="text-lg font-bold text-indigo-600">{offer.aria_score}</p>
                          <p className="text-[10px] text-slate-400">score</p>
                        </div>
                      )}
                      <Btn variant="success" size="xs" onClick={() => act(offer.id, 'approve', { slot: null })} loading={acting === offer.id}>
                        <CheckCircle className="w-3 h-3" /> Approve
                      </Btn>
                      <Btn variant="ghost" size="xs" onClick={() => act(offer.id, 'rerun-strategy')} loading={acting === offer.id}>
                        Re-research
                      </Btn>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {/* Portfolio Ranking */}
          {ranking.length > 0 && (
            <Card>
              <CardHeader title="Portfolio Ranking" subtitle="ARIA's priority order — top 3 get roster slots" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['Rank', 'Offer', 'Status', 'ARIA Score', 'Est. Revenue/mo', 'Action'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ranking.map((offer, i) => (
                      <tr key={offer.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${i < 3 ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{i + 1}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{offer.product_name}</p>
                          <a href={offer.affiliate_url} target="_blank" rel="noreferrer"
                             className="text-xs text-blue-500 font-mono hover:underline">
                            {offer.affiliate_url?.replace(/^https?:\/\//, '').slice(0, 40)}
                          </a>
                        </td>
                        <td className="px-4 py-3"><Badge label={offer.status} color={STATUS_COLOR[offer.status] || 'slate'} /></td>
                        <td className="px-4 py-3">{scoreBar(offer.aria_score)}</td>
                        <td className="px-4 py-3 font-medium text-emerald-700">{money(offer.estimated_monthly_revenue_usd)}</td>
                        <td className="px-4 py-3">
                          {offer.status !== 'active' && offer.status !== 'rejected' && (
                            <Btn variant="outline" size="xs" onClick={() => act(offer.id, 'approve', { slot: i < 3 ? i + 1 : null })} loading={acting === offer.id}>
                              Approve
                            </Btn>
                          )}
                          {offer.status === 'active' && (
                            <Btn variant="ghost" size="xs" onClick={() => act(offer.id, 'pause')} loading={acting === offer.id}>Pause</Btn>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {!roster.length && !pending.length && !ranking.length && (
            <Card>
              <EmptyState icon={Tag} title="No offers yet" subtitle='Click "Submit Offer" to add your first affiliate offer — ARIA will handle the research.' />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
// ─── VIEW: OFFERS ─────────────────────────────────────────────────────────────
function OffersView({ toast }) {
  const [tab, setTab] = useState('os');  // 'os' | 'config'
  const [offers,   setOffers]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);   // slug being edited
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState({});
  const [showNew,  setShowNew]  = useState(false);
  const [newSlug,  setNewSlug]  = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api('/api/offers');
    if (data?.offers) setOffers(data.offers);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const startEdit = (offer) => {
    setEditing(offer.offer_slug);
    setForm({ ...offer });
  };
  const cancelEdit = () => { setEditing(null); setForm({}); };
  const saveOffer = async () => {
    setSaving(true);
    const slug = editing || newSlug.trim().toLowerCase().replace(/\s+/g, '_');
    const { data, error } = await api(`/api/offers/${slug}`, {
      method: 'POST',
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (data) {
      toast('Saved', `Offer "${form.label}" updated`, 'success');
      setEditing(null); setShowNew(false); setNewSlug(''); setForm({});
      load();
    } else {
      toast('Save failed', error, 'error');
    }
  };
  const initTable = async () => {
    const { data, error } = await api('/api/offers/admin/init', { method: 'POST' });
    if (data) { toast('Table ready', `${data.rows} offers seeded`, 'success'); load(); }
    else toast('Init failed', error, 'error');
  };
  const OfferForm = ({ slugEditable }) => (
    <div className="space-y-3 mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
      {slugEditable && (
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Offer Slug (unique ID, no spaces)</label>
          <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            placeholder="e.g. ttc_homeowner_permits"
            value={newSlug} onChange={e => setNewSlug(e.target.value)} />
        </div>
      )}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Display Label</label>
        <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          value={form.label || ''} onChange={e => setForm(f => ({...f, label: e.target.value}))} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Calendly Booking URL</label>
        <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
          placeholder="https://calendly.com/jamie/your-event-type"
          value={form.booking_url || ''} onChange={e => setForm(f => ({...f, booking_url: e.target.value}))} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">ARIA Context (fed into reply drafts)</label>
        <textarea rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
          placeholder="What we're selling and why prospects should care..."
          value={form.offer_context || ''} onChange={e => setForm(f => ({...f, offer_context: e.target.value}))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Commission %</label>
          <input type="number" step="0.5" min="0" max="100"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            value={form.commission_pct || 15} onChange={e => setForm(f => ({...f, commission_pct: parseFloat(e.target.value)}))} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">From Name</label>
          <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            value={form.from_name || 'Jamie Murray'} onChange={e => setForm(f => ({...f, from_name: e.target.value}))} />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Email Signature</label>
        <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
          value={form.signature || ''} onChange={e => setForm(f => ({...f, signature: e.target.value}))} />
      </div>
      <div className="flex gap-2 pt-1">
        <Btn variant="primary" size="sm" onClick={saveOffer} loading={saving}>Save Offer</Btn>
        <Btn variant="ghost" size="sm" onClick={() => { cancelEdit(); setShowNew(false); }}>Cancel</Btn>
      </div>
    </div>
  );
  return (
    <div className="space-y-5">
      {/* Tab switcher */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit">
        {[['os', '🤖 ARIA Offer OS'], ['config', '⚙️ Offer Config']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${tab === id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'os' && <OfferOsSection toast={toast} />}
      {tab === 'config' && (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Btn variant="primary" size="md" onClick={() => { setShowNew(true); setEditing(null); setForm({ commission_pct: 15, from_name: 'Jamie Murray', active: true }); }}>
              + Add Config
            </Btn>
            <Btn variant="ghost" size="md" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Btn>
            <Btn variant="ghost" size="sm" onClick={initTable} title="Create table + seed if missing">⚙️ Init Table</Btn>
          </div>
          {showNew && !editing && (
            <Card>
              <CardHeader title="New Offer Config" subtitle="Booking URL and context for campaigns in this offer vertical" />
              <div className="px-6 pb-6"><OfferForm slugEditable /></div>
            </Card>
          )}
          <Card>
            <CardHeader
              title="Offer Configurations"
              subtitle="One row per sellable offer. ARIA picks the right booking URL, context, and signature per campaign vertical."
            />
            {loading ? <div className="flex justify-center py-12"><Spinner /></div> : (
              <div className="divide-y divide-slate-100">
                {offers.length === 0 && (
                  <div className="p-8 text-center">
                    <p className="text-slate-500 mb-3">No offer configs found.</p>
                    <Btn variant="outline" size="sm" onClick={initTable}>⚙️ Create Table + Seed Starter Configs</Btn>
                  </div>
                )}
                {offers.map(offer => (
                  <div key={offer.offer_slug} className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-800">{offer.label}</span>
                          <code className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">{offer.offer_slug}</code>
                          <Badge label={offer.active ? 'active' : 'inactive'} color={offer.active ? 'emerald' : 'slate'} />
                          <span className="text-xs text-slate-400">{offer.commission_pct}% commission</span>
                        </div>
                        <a href={offer.booking_url} target="_blank" rel="noreferrer"
                           className="text-sm text-blue-600 hover:underline font-mono truncate block max-w-xl">
                          {offer.booking_url}
                        </a>
                        {offer.offer_context && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{offer.offer_context}</p>
                        )}
                      </div>
                      <Btn variant="outline" size="sm" onClick={() => startEdit(offer)}>Edit</Btn>
                    </div>
                    {editing === offer.offer_slug && <OfferForm slugEditable={false} />}
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card>
            <CardHeader title="Vertical → Offer Mapping" subtitle="Which booking link ARIA uses for each campaign vertical" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {['Campaign Vertical', 'Offer Used'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ['aivideo_ad_agencies',     'arclight_ai_video'],
                    ['aivideo_real_estate',     'arclight_ai_video'],
                    ['aivideo_automotive',      'arclight_ai_video'],
                    ['aivideo_ecommerce',       'arclight_ai_video'],
                    ['aivideo_franchise',       'arclight_ai_video'],
                    ['aivideo_social_media',    'arclight_ai_video'],
                    ['aivideo_channel_partner', 'arclight_ai_video'],
                    ['ttc_agencies',            'ttc_ai_agent'],
                    ['ttc_saas',                'ttc_ai_agent'],
                    ['ttc_ecommerce',           'ttc_ai_agent'],
                  ].map(([vertical, offerSlug]) => (
                    <tr key={vertical} className="hover:bg-slate-50">
                      <td className="px-5 py-2.5 font-mono text-xs text-slate-600">{vertical}</td>
                      <td className="px-5 py-2.5">
                        <code className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{offerSlug}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
// ─── VIEW: CAMPAIGNS ─────────────────────────────────────────────────────────
function CampaignsView({ toast }) {
  const [campaigns, setCampaigns] = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [checking,  setChecking]  = useState(false);
  const [launching, setLaunching] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    // GET /api/campaigns/health returns { campaigns: [...], portfolio_summary: {...} }
    const { data } = await api('/api/campaigns/health');
    if (data?.campaigns) { setCampaigns(data.campaigns); setSummary(data.portfolio_summary); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const runCheck = async () => {
    setChecking(true);
    const controller = new AbortController();
    const tid = setTimeout(() => { controller.abort(); }, 30_000);
    try {
      const { data, error } = await api('/api/campaigns/health/run', { method: 'POST', signal: controller.signal });
      clearTimeout(tid);
      setChecking(false);
      if (data) { toast('Health check complete', data.message || 'Grades refreshed', 'success'); load(); }
      else toast('Health check failed', error || 'Unknown error', 'error');
    } catch (e) {
      clearTimeout(tid);
      setChecking(false);
      toast('Health check timed out', 'No response after 30s — the check may still be running in the background', 'error');
    }
  };
  const launchTTC = async () => {
    setLaunching(true);
    const { data, error } = await api('/api/ttc/launch-campaigns', { method: 'POST' });
    setLaunching(false);
    if (data) toast('Campaign queued', 'Check your email to review the HITL copy and approve', 'success');
    else toast('Launch failed', error, 'error');
  };
  const toggleCampaign = async (slug, currentlyActive) => {
    const path = currentlyActive ? `/api/campaigns/health/${slug}/pause` : `/api/campaigns/health/${slug}/activate`;
    const { data, error } = await api(path, { method: 'POST' });
    if (data) { toast(currentlyActive ? 'Paused' : 'Activated', slug, 'success'); load(); }
    else toast('Update failed', error, 'error');
  };
  // Portfolio summary tiles
  const portfolio = summary ? [
    { label: 'Total Campaigns', value: summary.total_campaigns ?? campaigns.length },
    { label: 'Avg Grade',       value: summary.average_grade ?? '—'               },
    { label: 'Urgent Actions',  value: summary.urgent_actions ?? 0                 },
    { label: 'Emails Sent',     value: (summary.total_emails_sent ?? 0).toLocaleString() },
    { label: 'Total Replies',   value: summary.total_replies ?? 0                 },
    { label: 'MRR Generated',   value: money(summary.total_mrr ?? 0)              },
  ] : [];
  return (
    <div className="space-y-5">
      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <Btn variant="primary" size="md" onClick={launchTTC} loading={launching}><Megaphone className="w-4 h-4" /> Launch AIVideo Agency Campaign</Btn>
        <Btn variant="outline" size="md" onClick={runCheck}  loading={checking}><Activity className="w-4 h-4" /> Run Health Check</Btn>
        <Btn variant="ghost"  size="md" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Btn>
      </div>
      {/* Portfolio summary row */}
      {portfolio.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {portfolio.map(({ label, value }) => (
            <Card key={label} className="p-4 text-center">
              <p className="text-xl font-bold text-slate-900">{value}</p>
              <p className="text-xs text-slate-500 mt-1">{label}</p>
            </Card>
          ))}
        </div>
      )}
      {/* Campaign table */}
      <Card>
        <CardHeader title="Campaign Health" subtitle="A (excellent) → F (pause now) · KPIs pulled from Instantly every 6h" />
        {loading ? <div className="flex justify-center py-16"><Spinner /></div>
          : campaigns.length === 0
            ? <div className="p-8"><EmptyState icon={Megaphone} title="No campaigns yet" subtitle="Launch your first TTC campaign — it will appear here after the next health check" /></div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['Campaign', 'Type', 'Grade', 'Sent', 'Open%', 'Reply%', 'Bounce%', 'Calls', 'MRR', 'ARIA Action', 'Control'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {campaigns.map((c, i) => <CampaignRow key={i} c={c} onToggle={toggleCampaign} />)}
                  </tbody>
                </table>
              </div>
            )
        }
      </Card>
    </div>
  );
}
function CampaignRow({ c, onToggle }) {
  // Response shape: c.name, c.slug, c.type, c.grade, c.aria_action, c.kpis.{emails_sent, open_rate_pct, reply_rate_pct, bounce_rate_pct, calls_booked, mrr_generated}
  const k = c.kpis || {};
  const isActive = c.aria_action !== 'pause_now' && c.aria_action !== 'monitor';
  const gCls = gradeColor(c.grade);
  const ACTION_COLOR = { scale: 'emerald', optimize: 'blue', monitor: 'amber', pause_now: 'red' };
  const actionColor = ACTION_COLOR[c.aria_action] || 'slate';
  return (
    <tr className="hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3.5">
        <p className="font-medium text-slate-800 max-w-[180px] truncate">{c.name || c.slug}</p>
        {c.grade_reason && <p className="text-[10px] text-slate-400 mt-0.5 max-w-[180px] truncate">{c.grade_reason}</p>}
      </td>
      <td className="px-4 py-3.5"><Badge label={c.type || '—'} color={c.type === 'ttc' ? 'indigo' : c.type === 'aivideo' ? 'purple' : 'slate'} /></td>
      <td className="px-4 py-3.5">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold border ${gCls}`}>{c.grade || '—'}</span>
      </td>
      <td className="px-4 py-3.5 text-slate-600">{(k.emails_sent ?? 0).toLocaleString()}</td>
      <td className="px-4 py-3.5 text-slate-600">{pct(k.open_rate_pct)}</td>
      <td className="px-4 py-3.5 text-slate-600">{pct(k.reply_rate_pct)}</td>
      <td className="px-4 py-3.5 text-slate-600">{pct(k.bounce_rate_pct)}</td>
      <td className="px-4 py-3.5 text-slate-600">{k.calls_booked ?? 0}</td>
      <td className="px-4 py-3.5 text-slate-600">{money(k.mrr_generated ?? 0)}</td>
      <td className="px-4 py-3.5"><Badge label={c.aria_action || '—'} color={actionColor} /></td>
      <td className="px-4 py-3.5">
        <Btn variant={isActive ? 'ghost' : 'success'} size="xs" onClick={() => onToggle(c.slug, isActive)}>
          {isActive ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Resume</>}
        </Btn>
      </td>
    </tr>
  );
}
// ─── VIEW: PIPELINE ───────────────────────────────────────────────────────────
function PipelineView() {
  const [tab,     setTab]     = useState('ttc');
  const [ttcLeads, setTtc]    = useState([]);
  const [alLeads,  setAl]     = useState([]);
  const [loading,  setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    const [t, a] = await Promise.all([api('/api/ttc/leads?limit=60'), api('/api/arclight/leads?limit=60')]);
    setTtc((t.data?.leads || t.data || []).map(l => ({ ...l, _offer: 'TTC' })));
    setAl((a.data?.leads || a.data || []).map(l => ({
      ...l, _offer: 'AIVideo',
      email: l.prospect_email,
      first_name: (l.prospect_name || '').split(' ')[0],
      last_name:  (l.prospect_name || '').split(' ').slice(1).join(' '),
      company:    l.prospect_company,
      title:      l.prospect_title,
    })));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const shown = tab === 'ttc' ? ttcLeads : alLeads;
  const STAGES = ['new','contacted','pitched','replied','call_booked','proposal','client'];
  const stageCount = s => shown.filter(l => (l.status || 'new') === s).length;
  const maxCount = Math.max(...STAGES.map(stageCount), 1);
  const STATUS_COLOR = { new: 'slate', contacted: 'blue', pitched: 'cyan', replied: 'indigo', discovery: 'purple', call_booked: 'amber', proposal: 'orange', client: 'emerald', declined: 'red' };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
          {[['ttc','🤝 Agency Partners'],['aivideo','🎬 AIVideo']].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === id ? 'bg-indigo-600 text-white shadow' : 'text-slate-500 hover:text-slate-800'}`}>{label}</button>
          ))}
        </div>
        <Btn variant="ghost" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Btn>
        <span className="text-sm text-slate-500">{shown.length} leads</span>
      </div>
      {/* Funnel */}
      <Card>
        <CardHeader title="Lead Funnel" subtitle={`${shown.length} total · ${stageCount('client')} clients`} />
        <div className="px-5 pb-5 pt-3">
          <div className="flex items-end gap-2 h-28">
            {STAGES.map(s => {
              const count = stageCount(s);
              const h = Math.max(Math.round((count / maxCount) * 96), count > 0 ? 6 : 2);
              return (
                <div key={s} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-bold text-slate-700">{count}</span>
                  <div className="w-full rounded-t-lg bg-indigo-400 transition-all" style={{ height: `${h}px` }} />
                  <span className="text-[10px] text-slate-500 capitalize text-center whitespace-nowrap">{s.replace('_', ' ')}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      {/* Lead table */}
      <Card>
        <CardHeader title="Lead List" subtitle="Most recent first" />
        {loading ? <div className="flex justify-center py-16"><Spinner /></div>
          : shown.length === 0
            ? <div className="p-8"><EmptyState icon={Users} title="No leads" subtitle="Load leads via the Agents view or Consulti endpoint" /></div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {['Name','Company','Title','Status','Email','MEDDIC','Last Activity'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {shown.slice(0, 50).map((l, i) => {
                      const mTier = l.meddic_tier;
                      const mColor = { HOT: 'red', WARM: 'amber', LUKEWARM: 'blue', COLD: 'slate' }[mTier] || 'slate';
                      // Sprint F: email verification badge
                      const emailDot = l.email_verified === true
                        ? <span title="Email verified" className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                        : l.email_verified === false
                          ? <span title={`Bounced (${l.email_status || 'invalid'})`} className="inline-block w-2 h-2 rounded-full bg-red-400" />
                          : <span title="Not yet verified" className="inline-block w-2 h-2 rounded-full bg-slate-300" />;
                      return (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3 font-medium text-slate-800">{[l.first_name, l.last_name].filter(Boolean).join(' ') || '—'}</td>
                        <td className="px-5 py-3 text-slate-600 max-w-[180px] truncate">{l.company || '—'}</td>
                        <td className="px-5 py-3 text-slate-500 max-w-[160px] truncate">{l.title || '—'}</td>
                        <td className="px-5 py-3"><Badge label={l.status || 'new'} color={STATUS_COLOR[l.status] || 'slate'} /></td>
                        <td className="px-5 py-3">{emailDot}</td>
                        <td className="px-5 py-3">{mTier ? <Badge label={mTier} color={mColor} /> : <span className="text-slate-300 text-xs">—</span>}</td>
                        <td className="px-5 py-3 text-slate-400 text-xs">{timeAgo(l.last_contacted_at || l.updated_at || l.created_at)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
        }
      </Card>
    </div>
  );
}
// ─── VIEW: AGENTS ─────────────────────────────────────────────────────────────
/**
 * Two panels:
 * 1. Conceptual agents from /api/agents/status (ARIA, Scout, etc.) — DB-backed
 * 2. APScheduler jobs from /api/scheduler/status — in-process, real execution
 */
// Trigger configs: map display card → actual endpoint + scheduler job ID
const TRIGGER_CONFIG = [
  { id: 'inbox_monitor',      label: 'Inbox Monitor',       emoji: '📬', desc: 'Classify all replies: TTC + AIVideo + ACX via Claude',      endpoint: '/api/inbox/scan',           method: 'POST', jobId: null },
  { id: 'campaign_health',    label: 'Campaign Health',     emoji: '🏥', desc: 'Grade campaigns A–F, auto-pause bad performers',            endpoint: '/api/campaigns/health/run', method: 'POST', jobId: 'campaign_health_check' },
  { id: 'outreach_push',      label: 'Outreach Push',       emoji: '📤', desc: 'Push queued leads to Instantly with AI first-line',         endpoint: '/api/outreach/run',          method: 'POST', jobId: 'campaign_push' },
  { id: 'lead_ingestion',     label: 'Lead Ingestion',      emoji: '🔍', desc: 'Load agency owners from Consulti (100 new leads) for AIVideo pitch',          endpoint: '/api/consulti/load-ttc-vertical?vertical_slug=ttc_agencies&limit=100', method: 'POST', jobId: 'lead_ingestion' },
  { id: 'aivideo_scout',      label: 'AIVideo Scout',       emoji: '🎬', desc: 'Load agency leads & queue campaign for Jamie+Emily review',             endpoint: '/api/aivideo/launch-scout/marketing_agencies', method: 'POST', jobId: null },
  { id: 'bounce_check',       label: 'Bounce Check',        emoji: '🛡', desc: 'Circuit-breaker: pauses campaigns > 5% bounce rate',       endpoint: '/api/scheduler/run/bounce_check', method: 'POST', jobId: 'bounce_check' },
  { id: 'analytics_sync',     label: 'Analytics Sync',      emoji: '📊', desc: 'Pull fresh open/reply/bounce stats from Instantly',         endpoint: '/api/scheduler/run/analytics_sync', method: 'POST', jobId: 'analytics_sync' },
  { id: 'morning_briefing',   label: 'Morning Briefing',    emoji: '☀️', desc: 'Generate and email daily strategic summary to Jamie',      endpoint: '/api/scheduler/run/morning_briefing', method: 'POST', jobId: 'morning_briefing' },
];
const AGENT_META = {
  aria:             { label: 'ARIA (COO)',       emoji: '🧠', desc: 'Central intelligence — research, orchestration, escalation' },
  scout:            { label: 'Scout',            emoji: '🔍', desc: 'Multi-source lead discovery' },
  signal_detector:  { label: 'Signal Detector',  emoji: '📡', desc: 'Detects intent signals from prospects and market data' },
  matchmaker:       { label: 'Matchmaker',       emoji: '🤝', desc: 'Matches qualified leads to the right offer' },
  outreach_captain: { label: 'Outreach Captain', emoji: '📧', desc: 'Manages Instantly campaigns and reply classification' },
  deal_closer:      { label: 'Deal Closer',      emoji: '🏆', desc: 'Drives proposals, proposals and client conversion' },
};
function AgentsView({ agents, toast }) {
  const [trigStates, setTrigStates] = useState({});   // id → 'running' | 'done' | 'error'
  const [schedStatus, setSchedStatus] = useState(null);
  const [schedLoading, setSchedLoading] = useState(false);
  const fetchSched = useCallback(async () => {
    setSchedLoading(true);
    const { data } = await api('/api/scheduler/status');
    if (data) setSchedStatus(data);
    setSchedLoading(false);
  }, []);
  useEffect(() => { fetchSched(); }, [fetchSched]);
  const runTrigger = async (cfg) => {
    setTrigStates(p => ({ ...p, [cfg.id]: 'running' }));
    const { data, error } = await api(cfg.endpoint, { method: cfg.method });
    if (data) {
      toast(`${cfg.label} complete`, data.message || data.summary || 'Done', 'success');
      setTrigStates(p => ({ ...p, [cfg.id]: 'done' }));
      fetchSched();
    } else {
      toast(`${cfg.label} failed`, error || 'Check backend logs', 'error');
      setTrigStates(p => ({ ...p, [cfg.id]: 'error' }));
    }
    setTimeout(() => setTrigStates(p => ({ ...p, [cfg.id]: null })), 5000);
  };
  const runAll = async () => {
    const { data, error } = await api('/api/scheduler/run-all', { method: 'POST' });
    if (data) toast('All jobs triggered', `${data.count} jobs queued`, 'success');
    else toast('Failed', error, 'error');
    fetchSched();
  };
  // Map scheduler job IDs to next_run for display
  const schedMap = {};
  if (schedStatus?.jobs) schedStatus.jobs.forEach(j => { schedMap[j.id] = j; });
  return (
    <div className="space-y-6">
      {/* APScheduler panel */}
      <Card>
        <CardHeader
          title="APScheduler — Background Jobs"
          subtitle={schedStatus?.running ? `Running · ${schedStatus.jobs?.length ?? 0} jobs registered` : 'Not running'}
          action={
            <div className="flex gap-2">
              <Btn variant="outline" size="sm" onClick={fetchSched} loading={schedLoading}><RefreshCw className="w-3.5 h-3.5" /></Btn>
              <Btn variant="primary" size="sm" onClick={runAll}><Power className="w-3.5 h-3.5" /> Run All Now</Btn>
            </div>
          }
        />
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {TRIGGER_CONFIG.map(cfg => {
            const ts    = trigStates[cfg.id];
            const sJob  = cfg.jobId ? schedMap[cfg.jobId] : null;
            const isRunning = ts === 'running';
            return (
              <div key={cfg.id} className={`p-4 rounded-xl border transition-all ${isRunning ? 'border-indigo-300 bg-indigo-50' : ts === 'done' ? 'border-emerald-200 bg-emerald-50' : ts === 'error' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{cfg.emoji}</span>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{cfg.label}</p>
                      {sJob?.next_run && <p className="text-[10px] text-slate-400">Next: {timeFrom(sJob.next_run)}</p>}
                    </div>
                  </div>
                  <Btn variant={ts === 'done' ? 'success' : 'primary'} size="xs" loading={isRunning} onClick={() => runTrigger(cfg)}>
                    {ts === 'done' ? <CheckCircle className="w-3 h-3" /> : ts === 'error' ? <XCircle className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                    {ts === 'done' ? 'Done' : ts === 'error' ? 'Error' : 'Run'}
                  </Btn>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{cfg.desc}</p>
              </div>
            );
          })}
        </div>
      </Card>
      {/* Conceptual agents from DB */}
      <Card>
        <CardHeader title="Agent Registry" subtitle="DB-backed run history — aria/scout/signal_detector/matchmaker/outreach_captain/deal_closer" />
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.length === 0
            ? <div className="col-span-3"><EmptyState icon={Bot} title="No agent runs yet" subtitle="Trigger jobs above to populate run history" /></div>
            : agents.map(a => {
                const meta = AGENT_META[a.agent] || { label: a.agent, emoji: '🤖', desc: '' };
                return (
                  <div key={a.agent} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{meta.emoji}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900">{meta.label}</p>
                          <StatusDot status={a.last_run_status || 'idle'} />
                        </div>
                        <p className="text-xs text-slate-500">{a.is_enabled ? 'Enabled' : 'Disabled'}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">{meta.desc}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white rounded-lg p-2"><p className="text-slate-400">Last run</p><p className="font-medium text-slate-700">{timeAgo(a.last_run_at)}</p></div>
                      <div className="bg-white rounded-lg p-2"><p className="text-slate-400">Items found</p><p className="font-medium text-slate-700">{a.items_found ?? 0}</p></div>
                      <div className="bg-white rounded-lg p-2"><p className="text-slate-400">Last cost</p><p className="font-medium text-slate-700">{a.cost_last_run ? `$${Number(a.cost_last_run).toFixed(4)}` : '—'}</p></div>
                      <div className="bg-white rounded-lg p-2"><p className="text-slate-400">MTD spend</p><p className="font-medium text-slate-700">{a.monthly_spend ? `$${Number(a.monthly_spend).toFixed(2)}` : '—'} / ${a.monthly_cap ?? 50}</p></div>
                    </div>
                  </div>
                );
              })
          }
        </div>
      </Card>
    </div>
  );
}
// ─── VIEW: CHAT ───────────────────────────────────────────────────────────────
const QUICK_PROMPTS = [
  "What needs my attention today?",
  "Show me the campaign health report",
  "How many TTC replies did we get this week?",
  "Who are the hottest leads right now?",
  "What's our revenue run rate?",
  "What should I greenlight next?",
  "Run me through the pipeline health",
  "Any campaigns I should pause?",
];
// ─── VIEW: FORGE ─────────────────────────────────────────────────────────────
function ForgeView({ toast }) {
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [url, setUrl] = useState('');
  const [productName, setProductName] = useState('');
  const [retailPrice, setRetailPrice] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const [d, p] = await Promise.all([
      api('/api/forge/dashboard'),
      api('/api/forge/products'),
    ]);
    setDashboard(d.data);
    setProducts(p.data?.products || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!url.trim()) return;
    setSubmitting(true);
    const { data, error } = await api('/api/forge/submit', {
      method: 'POST',
      body: JSON.stringify({
        alibaba_url: url.trim(),
        product_name: productName || undefined,
        estimated_retail_usd: retailPrice ? parseFloat(retailPrice) : undefined,
      }),
    });
    setSubmitting(false);
    if (error) { toast?.('error', error); return; }
    toast?.('ok', data.status === 'exists' ? 'Already in pipeline' : 'Product submitted for evaluation');
    setUrl(''); setProductName(''); setRetailPrice('');
    load();
  };

  const makeDecision = async (productId, decision) => {
    const { data, error } = await api(`/api/forge/decision/${productId}`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    });
    if (error) { toast?.('error', error); return; }
    toast?.('ok', `Decision: ${decision}`);
    load();
    setSelected(null);
  };

  const statusColor = (s) => ({
    sourcing: 'bg-yellow-100 text-yellow-700',
    evaluated: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    archived: 'bg-slate-100 text-slate-500',
  }[s] || 'bg-slate-100 text-slate-500');

  const decisionColor = (d) => ({
    go: 'text-green-600 font-bold',
    maybe: 'text-yellow-600 font-bold',
    'no-go': 'text-red-600 font-bold',
  }[d] || '');

  if (loading) return <div className="p-8 text-slate-400">Loading FORGE...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <PackageSearch className="w-7 h-7 text-indigo-600" /> FORGE
          </h1>
          <p className="text-slate-500 mt-0.5">Product sourcing engine — Alibaba to Go/No-Go in minutes</p>
        </div>
        <button onClick={load} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Total Products', value: dashboard.total_products, color: 'text-slate-700' },
            { label: 'Approved (Go)', value: dashboard.approved_products, color: 'text-green-600' },
            { label: 'Avg Margin', value: dashboard.avg_margin_pct ? `${dashboard.avg_margin_pct}%` : '—', color: 'text-indigo-600' },
            { label: 'Pending Eval', value: dashboard.by_status?.sourcing || 0, color: 'text-yellow-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value ?? '—'}</p>
            </div>
          ))}
        </div>
      )}

      {/* Submit form */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-semibold text-slate-800 mb-3">Submit Product URL</h2>
        <div className="grid grid-cols-3 gap-3">
          <input
            className="col-span-3 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            placeholder="Alibaba product URL..."
            value={url} onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
          />
          <input
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            placeholder="Product name (optional)"
            value={productName} onChange={e => setProductName(e.target.value)}
          />
          <input
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            placeholder="Est. retail price USD (optional)"
            type="number" value={retailPrice} onChange={e => setRetailPrice(e.target.value)}
          />
          <button
            onClick={submit} disabled={submitting || !url.trim()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Submit'}
          </button>
        </div>
      </div>

      {/* Products table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800">Product Pipeline ({products.length})</h2>
        </div>
        {products.length === 0 ? (
          <p className="p-8 text-center text-slate-400">No products yet — submit an Alibaba URL above</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {['Product', 'Category', 'Retail Price', 'Margin', 'Decision', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {products.map(p => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <a href={p.alibaba_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center gap-1">
                      {p.product_name || `Product #${p.id}`}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.category || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{p.estimated_retail_usd ? `$${p.estimated_retail_usd}` : '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{p.margin_potential_pct ? `${p.margin_potential_pct.toFixed(1)}%` : '—'}</td>
                  <td className={`px-4 py-3 ${decisionColor(p.go_nogo_decision)}`}>{p.go_nogo_decision || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(p.status)}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {p.status === 'evaluated' && !p.go_nogo_decision && (
                      <div className="flex gap-1">
                        <button onClick={() => makeDecision(p.id, 'go')} className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs hover:bg-green-200">Go</button>
                        <button onClick={() => makeDecision(p.id, 'maybe')} className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs hover:bg-yellow-200">Maybe</button>
                        <button onClick={() => makeDecision(p.id, 'no-go')} className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs hover:bg-red-200">No-Go</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── VIEW: LAUNCHPAD ─────────────────────────────────────────────────────────
function LaunchpadView({ toast }) {
  const [dashboard, setDashboard] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ brand_name: '', tagline: '', mission_statement: '', target_market: '', brand_voice: '' });
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandDetail, setBrandDetail] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [assetType, setAssetType] = useState('email_copy');

  const load = async () => {
    setLoading(true);
    const [d, b] = await Promise.all([
      api('/api/launchpad/dashboard'),
      api('/api/launchpad/brands'),
    ]);
    setDashboard(d.data);
    setBrands(b.data?.brands || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const loadBrand = async (id) => {
    const { data } = await api(`/api/launchpad/brands/${id}`);
    setBrandDetail(data);
    setSelectedBrand(id);
  };

  const createBrand = async () => {
    if (!form.brand_name.trim()) return;
    setSubmitting(true);
    const { data, error } = await api('/api/launchpad/intake', {
      method: 'POST',
      body: JSON.stringify(form),
    });
    setSubmitting(false);
    if (error) { toast?.('error', error); return; }
    toast?.('ok', `Brand "${form.brand_name}" created with draft strategy`);
    setForm({ brand_name: '', tagline: '', mission_statement: '', target_market: '', brand_voice: '' });
    setShowForm(false);
    load();
    if (data?.brand_id) loadBrand(data.brand_id);
  };

  const approveStrategy = async (strategyId) => {
    const { error } = await api(`/api/launchpad/strategies/${strategyId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved_by: 'jamie' }),
    });
    if (error) { toast?.('error', error); return; }
    toast?.('ok', 'Strategy approved — brand ready for asset generation');
    if (selectedBrand) loadBrand(selectedBrand);
    load();
  };

  const generateAsset = async () => {
    if (!selectedBrand) return;
    setGenerating(true);
    const { data, error } = await api('/api/launchpad/assets/generate', {
      method: 'POST',
      body: JSON.stringify({ launch_brand_id: selectedBrand, asset_type: assetType }),
    });
    setGenerating(false);
    if (error) { toast?.('error', error); return; }
    toast?.('ok', `${assetType.replace('_', ' ')} generated`);
    loadBrand(selectedBrand);
  };

  const approveAsset = async (assetId) => {
    const { error } = await api(`/api/launchpad/assets/${assetId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved_by: 'jamie' }),
    });
    if (error) { toast?.('error', error); return; }
    toast?.('ok', 'Asset approved');
    if (selectedBrand) loadBrand(selectedBrand);
  };

  const statusColor = (s) => ({
    drafting: 'bg-slate-100 text-slate-500',
    strategy_pending: 'bg-yellow-100 text-yellow-700',
    strategy_approved: 'bg-blue-100 text-blue-700',
    ready_for_copy: 'bg-indigo-100 text-indigo-700',
    approved: 'bg-green-100 text-green-700',
    archived: 'bg-slate-100 text-slate-400',
  }[s] || 'bg-slate-100 text-slate-500');

  if (loading) return <div className="p-8 text-slate-400">Loading LAUNCHPAD...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Rocket className="w-7 h-7 text-indigo-600" /> LAUNCHPAD
          </h1>
          <p className="text-slate-500 mt-0.5">Brand launch engine — intake to approved assets in minutes</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <PlusCircle className="w-4 h-4" /> New Brand
        </button>
      </div>

      {/* Stats */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Total Brands', value: dashboard.total_brands },
            { label: 'Total Assets', value: dashboard.total_assets },
            { label: 'Pending Review', value: dashboard.assets_pending_review, color: dashboard.assets_pending_review > 0 ? 'text-amber-600' : 'text-slate-700' },
            { label: 'Approved', value: dashboard.brands_by_status?.approved || 0, color: 'text-green-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-2xl font-bold mt-1 ${s.color || 'text-slate-700'}`}>{s.value ?? '—'}</p>
            </div>
          ))}
        </div>
      )}

      {/* New brand form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-indigo-200 p-5 space-y-3">
          <h2 className="font-semibold text-slate-800">Brand Intake</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'brand_name', placeholder: 'Brand name *', required: true },
              { key: 'tagline', placeholder: 'Tagline (1 line)' },
              { key: 'target_market', placeholder: 'Target market (e.g. SaaS founders, homeowners...)' },
              { key: 'brand_voice', placeholder: 'Brand voice (e.g. professional, friendly, bold...)' },
            ].map(f => (
              <input key={f.key}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder={f.placeholder}
                value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              />
            ))}
            <textarea
              className="col-span-2 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              placeholder="Mission statement (optional)"
              rows={2}
              value={form.mission_statement} onChange={e => setForm(p => ({ ...p, mission_statement: e.target.value }))}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={createBrand} disabled={submitting || !form.brand_name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Brand + Draft Strategy'}
            </button>
            <button onClick={() => setShowForm(false)} className="text-slate-500 hover:text-slate-700 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        {/* Brand list */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-sm text-slate-700">Brands ({brands.length})</div>
          {brands.length === 0 ? (
            <p className="p-6 text-center text-slate-400 text-sm">No brands yet</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {brands.map(b => (
                <button key={b.id} onClick={() => loadBrand(b.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${selectedBrand === b.id ? 'bg-indigo-50' : ''}`}>
                  <p className="font-medium text-slate-800 text-sm">{b.brand_name}</p>
                  {b.tagline && <p className="text-xs text-slate-500 mt-0.5 truncate">{b.tagline}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColor(b.status)}`}>{b.status?.replace(/_/g, ' ')}</span>
                    {b.asset_count > 0 && <span className="text-xs text-slate-400">{b.asset_count} assets</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Brand detail */}
        <div className="col-span-2 space-y-4">
          {!brandDetail ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
              Select a brand to see details
            </div>
          ) : (
            <>
              {/* Strategy section */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3">GTM Strategy</h3>
                {brandDetail.strategies?.length === 0 ? (
                  <p className="text-slate-400 text-sm">No strategy generated yet</p>
                ) : brandDetail.strategies?.map(s => (
                  <div key={s.id} className="space-y-2">
                    <p className="text-sm text-slate-700"><span className="font-medium">Positioning:</span> {s.positioning_statement}</p>
                    {s.key_messages?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Key Messages:</p>
                        <ul className="space-y-0.5">
                          {s.key_messages.map((m, i) => <li key={i} className="text-xs text-slate-600">• {m}</li>)}
                        </ul>
                      </div>
                    )}
                    {s.primary_channels?.length > 0 && (
                      <p className="text-sm text-slate-600"><span className="font-medium">Channels:</span> {s.primary_channels.join(', ')}</p>
                    )}
                    {s.aria_recommendation && (
                      <p className="text-xs text-indigo-600 bg-indigo-50 rounded p-2">{s.aria_recommendation}</p>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {s.status}
                      </span>
                      {s.status === 'draft' && (
                        <button onClick={() => approveStrategy(s.id)}
                          className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-lg">
                          Approve Strategy
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Asset generation */}
              {(brandDetail.status === 'ready_for_copy' || brandDetail.status === 'approved') && (
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-800 mb-3">Generate Asset</h3>
                  <div className="flex gap-2">
                    <select
                      value={assetType} onChange={e => setAssetType(e.target.value)}
                      className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {['email_copy', 'landing_page', 'ad_creative', 'social_post', 'video_script', 'brand_image'].map(t => (
                        <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                    <button onClick={generateAsset} disabled={generating}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1">
                      {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4" /> Generate</>}
                    </button>
                  </div>
                </div>
              )}

              {/* Assets list */}
              {brandDetail.assets?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 font-semibold text-sm text-slate-700">
                    Assets ({brandDetail.assets.length})
                  </div>
                  <div className="divide-y divide-slate-100">
                    {brandDetail.assets.map(a => (
                      <div key={a.id} className="px-5 py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{a.asset_name || a.asset_type}</p>
                          <p className="text-xs text-slate-500">{a.asset_type.replace(/_/g, ' ')} · {a.generated_by_agent}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            a.status === 'approved' ? 'bg-green-100 text-green-700' :
                            a.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                            a.status === 'published' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-500'
                          }`}>{a.status}</span>
                          {a.status === 'pending_review' && (
                            <button onClick={() => approveAsset(a.id)}
                              className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded">
                              Approve
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatView({ messages, onSend, loading }) {
  const [input, setInput] = useState('');
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  const send = () => { if (!input.trim()) return; onSend(input.trim()); setInput(''); };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  return (
    <div className="flex flex-col h-[calc(100vh-130px)]">
      <div className="flex-1 overflow-y-auto space-y-4 pb-4 pr-1">
        {messages.map((m, i) => <ChatBubble key={i} m={m} />)}
        {loading && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm">🤖</div>
            <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
              <div className="flex gap-1.5">{[0,1,2].map(i => <div key={i} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}</div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 flex-wrap mb-3">
        {QUICK_PROMPTS.map(p => (
          <button key={p} onClick={() => onSend(p)} className="text-xs bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 px-3 py-1.5 rounded-full transition-colors border border-transparent hover:border-indigo-200">{p}</button>
        ))}
      </div>
      <div className="flex gap-3 bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
        <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey} rows={1}
          placeholder="Ask ARIA anything about your pipeline, campaigns, leads, or what to greenlight next…"
          className="flex-1 resize-none text-sm text-slate-800 placeholder-slate-400 focus:outline-none leading-6" />
        <button onClick={send} disabled={!input.trim() || loading}
          className="p-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl disabled:opacity-40 transition-colors flex-shrink-0 self-end">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
function ChatBubble({ m }) {
  const isARIA = m.role === 'aria';
  return (
    <div className={`flex items-start gap-3 ${isARIA ? '' : 'flex-row-reverse'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${isARIA ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
        {isARIA ? '🤖' : 'J'}
      </div>
      <div className={`max-w-[75%] ${isARIA ? '' : 'items-end'}`}>
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${isARIA ? 'bg-white border border-slate-200 text-slate-800 rounded-tl-none' : 'bg-indigo-600 text-white rounded-tr-none'}`}>
          {isARIA ? <Md>{m.text}</Md> : <p className="whitespace-pre-wrap">{m.text}</p>}
        </div>
        {m.model && <p className="text-[10px] text-slate-400 mt-1 px-1">{m.model}{m.cost ? ` · $${m.cost.toFixed(5)}` : ''}</p>}
      </div>
    </div>
  );
}
// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  // /va route → standalone VA dashboard (no Jamie sidebar/header)
  if (typeof window !== 'undefined' && window.location.pathname.replace(/\/$/, '') === '/va') {
    return <VADashboard />;
  }
  const [view,      setView]      = useState('overview');
  const [collapsed, setCollapsed] = useState(false);
  const { toasts, add: toast, dismiss } = useToasts();
  // Core data
  const [kpis,        setKpis]        = useState({ pipeline_value: 0, commission_this_month: 0, commission_ytd: 0, campaigns_active: 0, tasks_pending: 0, tasks_overdue: 0, ai_cost_this_month: 0 });
  const [tasks,       setTasks]       = useState([]);
  const [agents,      setAgents]      = useState([]);
  const [report,      setReport]      = useState('');
  const [reportLoading, setRptLoad]   = useState(true);
  const [connected,   setConnected]   = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [inboxCount,  setInboxCount]  = useState(0);
  // Chat
  const [chatMsgs, setChatMsgs] = useState([
    { role: 'aria', text: "Good morning. ARIA is online and monitoring your pipeline.\n\nI track your campaigns, classify every reply, grade campaign health A–F, and surface anything that needs your greenlight. Ask me anything or navigate the sidebar to drill in." }
  ]);
  const [chatLoading, setChatLoading] = useState(false);
  const [actioning,   setActioning]   = useState(null);
  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchKPIs = useCallback(async () => {
    const { data } = await api('/api/dashboard/kpis');
    if (data) { setKpis(data); setConnected(true); setLastRefresh(new Date()); }
  }, []);
  const fetchTasks = useCallback(async () => {
    const { data } = await api('/api/tasks');
    // Backend: { tasks: [{id, title, description, category, priority, status, aria_recommendation, sla_deadline, is_overdue, agent_source, ...}] }
    if (data?.tasks) setTasks(data.tasks);
    else if (Array.isArray(data)) setTasks(data);
  }, []);
  const fetchAgents = useCallback(async () => {
    const { data } = await api('/api/agents/status');
    // Backend: { agents: [{agent, is_enabled, last_run_status, last_run_at, items_found, cost_last_run, monthly_spend, monthly_cap}] }
    if (data?.agents) setAgents(data.agents);
  }, []);
  const fetchReport = useCallback(async () => {
    setRptLoad(true);
    const { data } = await api('/api/dashboard/daily-report');
    if (data?.recommendations) setReport(data.recommendations);
    else if (data?.html_content) setReport(data.html_content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim());
    setRptLoad(false);
  }, []);
  const fetchInboxCount = useCallback(async () => {
    const { data } = await api('/api/inbox/replies');
    const count = data?.total || data?.replies?.length || 0;
    setInboxCount(count);
  }, []);
  // ── Init + polling ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetchKPIs();
    fetchTasks();
    fetchAgents();
    fetchReport();
    fetchInboxCount();
    const t1 = setInterval(fetchKPIs,        60_000);
    const t2 = setInterval(fetchTasks,        30_000);
    const t3 = setInterval(fetchAgents,       20_000);
    const t4 = setInterval(fetchInboxCount,   60_000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); clearInterval(t4); };
  }, [fetchKPIs, fetchTasks, fetchAgents, fetchReport, fetchInboxCount]);
  // ── Task actions ────────────────────────────────────────────────────────────
  const handleApprove = useCallback(async (taskId, note = '') => {
    setActioning(`${taskId}-approve`);
    const { data, error } = await api(`/api/tasks/${taskId}/action`, { method: 'POST', body: JSON.stringify({ decision: 'approve', notes: note }) });
    setActioning(null);
    if (data) {
      toast('Approved ✓', '', 'success');
      setTasks(p => p.map(t => t.id === taskId ? { ...t, status: 'approved' } : t));
      setTimeout(fetchKPIs, 800);
    } else { toast('Approval failed', error, 'error'); }
  }, [toast, fetchKPIs]);
  const handleReject = useCallback(async (taskId, note = '') => {
    setActioning(`${taskId}-reject`);
    const { data, error } = await api(`/api/tasks/${taskId}/action`, { method: 'POST', body: JSON.stringify({ decision: 'reject', notes: note }) });
    setActioning(null);
    if (data) {
      toast('Rejected', '', 'info');
      setTasks(p => p.map(t => t.id === taskId ? { ...t, status: 'rejected' } : t));
      setTimeout(fetchKPIs, 800);
    } else { toast('Action failed', error, 'error'); }
  }, [toast, fetchKPIs]);
  // ── Chat ────────────────────────────────────────────────────────────────────
  const handleSendChat = useCallback(async (msg) => {
    const history = chatMsgs.map(m => ({ role: m.role === 'aria' ? 'assistant' : 'user', content: m.text }));
    setChatMsgs(p => [...p, { role: 'jamie', text: msg }]);
    setChatLoading(true);
    const { data, error } = await api('/api/chat', { method: 'POST', body: JSON.stringify({ message: msg, history: history.slice(-12) }) });
    setChatLoading(false);
    setChatMsgs(p => [...p, {
      role: 'aria',
      text: data?.response || (error ? `Backend error: ${error}` : "Not connected to backend yet."),
      model: data?.model_used,
      cost: data?.cost_usd,
    }]);
  }, [chatMsgs]);
  // ── Refresh all ─────────────────────────────────────────────────────────────
  const refreshAll = useCallback(async () => {
    toast('Refreshing…', '', 'info');
    await Promise.all([fetchKPIs(), fetchTasks(), fetchAgents(), fetchReport(), fetchInboxCount()]);
    toast('Refreshed', '', 'success');
  }, [toast, fetchKPIs, fetchTasks, fetchAgents, fetchReport, fetchInboxCount]);
  // ── Derived ─────────────────────────────────────────────────────────────────
  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const VIEW_TITLE = { overview: 'Overview', approvals: 'Approvals Queue', inbox: 'Reply Inbox', campaigns: 'Campaign Control', offers: 'Offer OS', pipeline: 'Lead Pipeline', agents: 'Agent Swarm', chat: 'ARIA Chat' };
  // ── Package icon for Offers (lucide doesn't export Package directly in some versions)
  return (
    <div className="flex h-screen bg-gray-950 text-gray-100 font-sans overflow-hidden">

      {/* ── Toast notifications ───────────────────────────────────────────── */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id}
            className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl shadow-lg text-sm max-w-sm
              ${t.type === 'success' ? 'bg-green-900/80 border border-green-700 text-green-100' :
                t.type === 'error'   ? 'bg-red-900/80 border border-red-700 text-red-100' :
                                       'bg-gray-800 border border-gray-700 text-gray-100'}`}>
            <span className="flex-1 font-medium">{t.title}</span>
            {t.message && <span className="text-xs opacity-75 mt-0.5">{t.message}</span>}
            <button onClick={() => dismiss(t.id)} className="ml-2 opacity-60 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className={`${collapsed ? 'w-16' : 'w-56'} flex-shrink-0 transition-all duration-200 bg-gray-900 border-r border-gray-800 flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-800 flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
            <Zap className="w-4 h-4 text-white" />
          </div>
          {!collapsed && (
            <div>
              <div className="font-bold text-white text-sm leading-tight">ARIA</div>
              <div className="text-xs text-gray-500 leading-tight">Command Center</div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {[
            { key: 'overview',  label: 'Overview',   Icon: LayoutDashboard },
            { key: 'approvals', label: 'Approvals',  Icon: CheckCircle,     badge: pendingCount },
            { key: 'inbox',     label: 'Inbox',      Icon: Inbox,           badge: inboxCount },
            { key: 'campaigns', label: 'Campaigns',  Icon: Megaphone },
            { key: 'offers',    label: 'Offers',     Icon: Tag },
            { key: 'pipeline',  label: 'Pipeline',   Icon: GitBranch },
            { key: 'agents',    label: 'Agents',     Icon: Bot },
            { key: 'chat',      label: 'ARIA Chat',  Icon: MessageSquare },
          ].map(({ key, label, Icon, badge }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors relative
                ${view === key
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span className="flex-1 text-left">{label}</span>}
              {!collapsed && badge > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none min-w-[1.2rem] text-center">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(c => !c)}
          className="p-4 text-gray-500 hover:text-white text-xs border-t border-gray-800 flex items-center gap-2"
        >
          {collapsed
            ? <ChevronRight className="w-4 h-4" />
            : <><ChevronLeft className="w-4 h-4" /><span>Collapse</span></>}
        </button>
      </aside>

      {/* ── Main area ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header */}
        <header className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-lg font-semibold text-white">{VIEW_TITLE[view]}</h1>
            <div className="text-xs text-gray-500">Total Trust Consulting · ARIA v2</div>
          </div>
          <div className="flex items-center gap-4">
            {connected
              ? <span className="text-xs text-green-400 flex items-center gap-1"><Wifi className="w-3 h-3" /> Live</span>
              : <span className="text-xs text-yellow-400 flex items-center gap-1"><WifiOff className="w-3 h-3" /> Connecting…</span>}
            <span className="text-xs text-gray-500">
              Loop: <span className="text-gray-300">Jamie</span>{' '}
              <span className="text-gray-500">Emily</span>
            </span>
            {lastRefresh && (
              <span className="text-xs text-gray-600">{timeAgo(lastRefresh.toISOString())}</span>
            )}
            <button
              onClick={refreshAll}
              className="text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
            >
              <RefreshCw className="w-3 h-3" /> Refresh all
            </button>
          </div>
        </header>

        {/* View content */}
        <main className="flex-1 overflow-y-auto bg-gray-950">
          {view === 'overview'  && (
            <OverviewView
              kpis={kpis} tasks={tasks} agents={agents}
              report={report} reportLoading={reportLoading}
              onNavigate={setView}
              onApprove={handleApprove} onReject={handleReject}
              actioning={actioning}
            />
          )}
          {view === 'approvals' && (
            <ApprovalsView
              tasks={tasks}
              onApprove={handleApprove} onReject={handleReject}
              actioning={actioning}
              toast={toast}
            />
          )}
          {view === 'inbox'    && <InboxView toast={toast} />}
          {view === 'campaigns'&& <CampaignsView toast={toast} />}
          {view === 'offers'   && <OffersView toast={toast} />}
          {view === 'pipeline'  && <PipelineView />}
          {view === 'forge'     && <ForgeView toast={toast} />}
          {view === 'launchpad' && <LaunchpadView toast={toast} />}
          {view === 'agents'   && <AgentsView agents={agents} toast={toast} />}
          {view === 'chat'     && (
            <ChatView messages={chatMsgs} onSend={handleSendChat} loading={chatLoading} />
          )}
        </main>
      </div>

      {/* ── Floating Ask ARIA button ──────────────────────────────────────── */}
      {view !== 'chat' && (
        <button
          onClick={() => setView('chat')}
          className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-full shadow-xl text-sm font-medium z-40 flex items-center gap-2 transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          Ask ARIA
        </button>
      )}
    </div>
  );
}
